import { fetchWithChatFeedback } from './chatFeedbackRequired';
import {
  Question,
  QA,
  MFR,
  Product,
  ProductWithPSC,
  ProductWithCodes,
  ProductWithVendors,
  RecommendedVendorInput,
  Vendor,
  ApiResponse,
} from '../types/pipeline';
import type { RawV2Turn } from '../components/FarChat/api';
import { apiCall, delay, type ApiResponse as TransportApiResponse } from './apiClient';
import { USER_FACING_ERROR_MESSAGES, extractDetailMessage, normalizeApiError } from '../utils/userFacingError';
import mockPscOutput from '../mockData/psc-output.json';
import { normalizeProductsWithVendors } from '../utils/vendorUtils';
import { normalizeProductMetadata } from '../utils/productMetadata';
import { stripInlineOverspecs } from '../utils/stripInlineOverspecs';
import { mapOverspecDecisions } from '../utils/mfrProjection';
import { catalogResearchSelected } from '../utils/sourceResearchSelections';

// Mock data configuration
const USE_MOCK_DATA = process.env.REACT_APP_USE_MOCK === 'true';
const MOCK_UP_TO_STAGE = parseInt(process.env.REACT_APP_MOCK_UP_TO_STAGE || '0');
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Helper to determine if a stage should use mock data
const shouldUseMock = (stageNumber: number): boolean => {
  if (stageNumber === 8) {
    return false; // Stage 8 must always hit real APIs
  }
  if (USE_MOCK_DATA) return true;
  return stageNumber <= MOCK_UP_TO_STAGE;
};

// Stage 1: Requirements Elicitation Functions

/** Display-safe metadata for the one optional Stage 1 source document. */
export interface RequirementDocumentMetadata {
  filename: string;
  content_type: string;
  upload_timestamp: string;
  /** Server-managed revision used only for cache identity, never displayed. */
  revision: number;
}

type RequirementDocumentResponse = {
  requirement_document: RequirementDocumentMetadata | null;
};

export interface RequirementDocumentExtraction {
  commodity_description: string;
  requirement_document: RequirementDocumentMetadata;
}

function requirementDocumentHeaders(): Record<string, string> {
  const userEmail = localStorage.getItem('userEmail') || (window as any).userEmail || '';
  return userEmail ? { 'X-User-Email': userEmail } : {};
}

async function throwRequirementDocumentError(response: Response, fallback: string): Promise<never> {
  let message = fallback;
  let errorCode: string | undefined;
  let technicalDetail: string | undefined;
  try {
    const body = await response.json();
    const detail = body?.detail && typeof body.detail === 'object' ? body.detail : body;
    message = extractDetailMessage(body?.detail ?? body?.error, fallback);
    errorCode = detail?.error_code ?? body?.error_code;
    technicalDetail = detail?.technical_detail ?? body?.technical_detail;
  } catch {
    // Keep the canonical client fallback for malformed response bodies.
  }
  const error = new Error(message) as Error & { error_code?: string; technical_detail?: string; status?: number };
  if (errorCode) error.error_code = errorCode;
  if (technicalDetail) error.technical_detail = technicalDetail;
  error.status = response.status;
  throw error;
}

/** Upload or replace the requirement document associated with a Market Research flow. */
export async function uploadRequirementDocument(
  flowId: string,
  file: File,
): Promise<RequirementDocumentMetadata> {
  const formData = new FormData();
  formData.append('flow_id', flowId);
  formData.append('file', file);

  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/requirements/upload-document`, {
    method: 'POST',
    headers: requirementDocumentHeaders(),
    body: formData,
    credentials: 'include',
  });
  if (!response.ok) await throwRequirementDocumentError(response, USER_FACING_ERROR_MESSAGES.reupload_document);
  const body = await response.json() as { data?: RequirementDocumentResponse };
  const document = body?.data?.requirement_document;
  if (!document) throw new Error(USER_FACING_ERROR_MESSAGES.reupload_document);
  return document;
}

/** Retrieve safe document metadata only; file bytes and storage data stay server-side. */
export async function getRequirementDocument(
  flowId: string,
): Promise<RequirementDocumentMetadata | null> {
  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/requirements/${encodeURIComponent(flowId)}/document`, {
    method: 'GET',
    headers: requirementDocumentHeaders(),
    credentials: 'include',
  });
  if (!response.ok) await throwRequirementDocumentError(response, USER_FACING_ERROR_MESSAGES.reupload_document);
  const body = await response.json() as { data?: RequirementDocumentResponse };
  return body?.data?.requirement_document ?? null;
}

/** Remove the current Stage 1 document without altering the separate typed requirement. */
export async function removeRequirementDocument(flowId: string): Promise<void> {
  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/requirements/${encodeURIComponent(flowId)}/document`, {
    method: 'DELETE',
    headers: requirementDocumentHeaders(),
    credentials: 'include',
  });
  if (!response.ok) await throwRequirementDocumentError(response, USER_FACING_ERROR_MESSAGES.reupload_document);
}

/** Extract the current server-retained source into the editable Stage 1 description. */
export async function extractRequirementDocument(
  flowId: string,
  confirmOverwrite: boolean,
): Promise<RequirementDocumentExtraction> {
  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/requirements/${encodeURIComponent(flowId)}/document/extract`, {
    method: 'POST',
    headers: {
      ...requirementDocumentHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ confirm_overwrite: confirmOverwrite }),
    credentials: 'include',
  });
  if (!response.ok) await throwRequirementDocumentError(response, USER_FACING_ERROR_MESSAGES.reupload_document);
  const body = await response.json() as { data?: Partial<RequirementDocumentExtraction> };
  const data = body?.data;
  if (!data || typeof data.commodity_description !== 'string' || !data.requirement_document) {
    throw new Error(USER_FACING_ERROR_MESSAGES.reupload_document);
  }
  return data as RequirementDocumentExtraction;
}

/**
 * Ask the Customer-only Stage 1 service to draft an editable description from
 * the already retained source document. The browser deliberately supplies no
 * document content, path, prompt, or model selection.
 */
export async function generateCustomerRequirementDescription(
  flowId: string,
  confirmOverwrite: boolean,
): Promise<RequirementDocumentExtraction> {
  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/requirements/${encodeURIComponent(flowId)}/document/generate-description`, {
    method: 'POST',
    headers: {
      ...requirementDocumentHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ confirm_overwrite: confirmOverwrite }),
    credentials: 'include',
  });
  if (!response.ok) await throwRequirementDocumentError(response, USER_FACING_ERROR_MESSAGES.reupload_document);
  const body = await response.json() as { data?: Partial<RequirementDocumentExtraction> };
  const data = body?.data;
  if (!data || typeof data.commodity_description !== 'string' || !data.requirement_document) {
    throw new Error(USER_FACING_ERROR_MESSAGES.reupload_document);
  }
  return data as RequirementDocumentExtraction;
}

// Save answers immediately for persistence
export async function saveAnswers(
  flowId: string,
  questions: Question[],
  answers: Record<string, string>,
  commodityDescription?: string
): Promise<ApiResponse<{ answers_saved: number; questions_count: number }>> {
  try {
    const response = await apiCall<{ answers_saved: number; questions_count: number }>(
      '/api/requirements/save-answers',
      'POST',
      {
        ...(flowId ? { flow_id: flowId } : {}),
        questions: questions,
        answers: answers,
        commodity_description: commodityDescription,
      }
    );
    
    return {
      success: response.success,
      data: response.data || { answers_saved: 0, questions_count: 0 },
      timestamp: new Date().toISOString(),
      error: response.error
    };
  } catch (error) {
    console.error('Failed to save answers:', error);
    return {
      success: false,
      data: { answers_saved: 0, questions_count: 0 },
      error: 'Failed to save answers',
      timestamp: new Date().toISOString()
    };
  }
}

export async function getRequirementQuestions(
  commodityDescription: string,
  flowId?: string | null,
  forceRecompute?: boolean
): Promise<ApiResponse<Question[]> & { answers?: Record<string, string> }> {
  // Explicit dev-mode opt-in: load hardcoded mock questions for offline testing only.
  if (shouldUseMock(1)) {
    await delay(1500); // Simulate API call

    // Generate questions based on commodity description
    const mockQuestions: Question[] = [
      {
        id: '1',
        category: 'features',
        question: `What specific tasks will this ${commodityDescription.split(' ')[0]} support?`,
        priority: 'High',
        helpText: 'Describe the primary use cases and operational scenarios',
      },
      {
        id: '2',
        category: 'performance',
        question: 'What are the minimum performance specifications required?',
        priority: 'High',
        helpText: 'Include metrics like speed, capacity, range, etc.',
      },
      {
        id: '3',
        category: 'performance',
        question: 'What environmental conditions must the system operate in?',
        priority: 'Medium',
        helpText: 'Temperature ranges, humidity, altitude, etc.',
      },
      {
        id: '4',
        category: 'compatibility',
        question: 'What existing systems must this integrate with?',
        priority: 'High',
        helpText: 'List current infrastructure and compatibility requirements',
      },
      {
        id: '5',
        category: 'features',
        question: 'Are there specific compliance or certification requirements?',
        priority: 'Medium',
        helpText: 'Military standards, industry certifications, etc.',
      },
      {
        id: '6',
        category: 'features',
        question: 'What level of vendor support and training is required?',
        priority: 'Low',
        helpText: 'Maintenance, training, documentation needs',
      },
    ];

    return {
      success: true,
      data: mockQuestions,
      timestamp: new Date().toISOString(),
    };
  }

  // Production path — no silent mock fallback. Surface backend errors to the UI.
  const response = await apiCall<Question[]>(
    '/api/requirements/questions',
    'POST',
    {
      commodity_description: commodityDescription,
      ...(flowId ? { flow_id: flowId } : {}),
      force_recompute: forceRecompute
    }
  ) as ApiResponse<Question[]> & { answers?: Record<string, string> };

  if (response.success) {
    return {
      ...response,
      timestamp: new Date().toISOString()
    };
  }

  // Backend signals upstream agent failure (e.g., LLM tool-call validation
  // failed or task stalled) by returning a 5xx with
  // `{"detail": "Questions could not be generated"}`. apiCall plumbs that
  // detail string through as `response.error`. Fall back to the literal
  // user-facing message if the detail was lost or garbled (network blip,
  // HTML error page, etc.) — the user must never see a raw stack trace or
  // opaque "500 Internal Server Error".
  const FALLBACK = 'Questions could not be generated';
  // apiClient now returns a friendly generic message ("An unexpected error
  // occurred. Please try again.") when the backend emits a bare 5xx with no
  // ApiResponse envelope, so we no longer need to filter out raw
  // "HTTP error! status: ..." strings here.
  const userMessage = response.error ?? response.message ?? FALLBACK;
  const err = new Error(userMessage);
  (err as any).error_code = response.error_code;
  (err as any).technical_detail = response.technical_detail;
  throw err;
}

export async function generateMinimalRequirements(
  questionsAndAnswers: QA[],
  commodityDescription?: string,
  flowId?: string | null,
  forceRecompute: boolean = false
): Promise<ApiResponse<MFR>> {
  console.log('[stageFunctions] generateMinimalRequirements called');
  console.log('[stageFunctions] commodityDescription:', commodityDescription);
  console.log('[stageFunctions] flowId:', flowId);

  // Production path — no silent mock fallback. Surface backend errors to the UI.
  console.log('[stageFunctions] Calling POST /api/requirements/generate-mfr');
  const response = await apiCall<any>(
    '/api/requirements/generate-mfr',
    'POST',
    {
      commodity_description: commodityDescription,
      questions_and_answers: questionsAndAnswers.map(qa => ({
        question: qa,
        answer: qa.answer
      })),
      ...(flowId ? { flow_id: flowId } : {}),
      force_recompute: forceRecompute
    }
  );

  if (response.success && response.data) {
    // Parse the minimum requirements text into structured format
    const mfrText = response.data.minimum_requirements || '';

    // Split the MFR text into individual requirements
    // Look for numbered patterns like "1. " or "2. "
    const reqPattern = /\d+\.\s\*\*[^:]+:\*\*/g;
    const parts = mfrText.split(reqPattern);

    // Extract the requirement titles and content
    const matches = mfrText.match(reqPattern) || [];
    const requirements: any = {
      functional: [],
      performance: [],
    };

    // If we have numbered requirements, parse them
    if (matches.length > 0) {
      matches.forEach((match: string, index: number) => {
        if (parts[index + 1]) {
          // Clean up the requirement text
          const reqTitle = match.replace(/\d+\.\s\*\*/, '').replace(/:\*\*/, '');
          const reqContent = stripInlineOverspecs(parts[index + 1]).trim();
          requirements.functional.push(`${reqTitle}: ${reqContent}`);
        }
      });
    } else if (mfrText.trim()) {
      // Fallback: split by sentences that look like requirements
      const sentences = mfrText.split(/(?<=[.!?])\s+/);
      requirements.functional = sentences.filter((s: string) => s.length > 20);
    }

    // Try to extract a title from the MFR text
    let title = commodityDescription?.substring(0, 100) || 'System Requirements';

    // Look for a title in the MFR text (usually starts with ## or ** and contains "Requirements")
    const titleMatch = mfrText.match(/##\s*(.+Requirements.+)/i) ||
                      mfrText.match(/\*\*(.+Requirements.+)\*\*/i);
    if (titleMatch) {
      title = titleMatch[1].trim();
    }

    const mfr: MFR = {
      id: `mfr-${Date.now()}`,
      commodityDescription: title,  // Use the extracted title instead of full description
      generatedAt: new Date().toISOString(),
      requirements: requirements,
      questionsAnswered: questionsAndAnswers,
      // Strip any legacy inline overspec / cost-savings block so the plain
      // editor only ever shows clean requirements text. Structured fields are
      // surfaced separately below.
      rawText: stripInlineOverspecs(mfrText),
      originalCommodityDescription: commodityDescription,  // Store original separately
      removedSpecs: Array.isArray(response.data.removed_specs)
        ? response.data.removed_specs
        : [],
      costSavingsEstimate: response.data.cost_savings_estimate || '',
      overspecDecisions: mapOverspecDecisions(response.data.overspec_decisions, Array.isArray(response.data.removed_specs)
        ? response.data.removed_specs.filter((item: unknown): item is string => typeof item === 'string')
        : []),
      stage2Revision: typeof response.data.stage2_revision === 'number'
        ? response.data.stage2_revision : undefined,
    };
    return {
      success: true,
      data: mfr,
      timestamp: new Date().toISOString()
    };
  }

  const err = new Error(response.error ?? USER_FACING_ERROR_MESSAGES.wait_and_retry);
  (err as any).error_code = response.error_code;
  (err as any).technical_detail = response.technical_detail;
  throw err;
}

// Save edited MFR to cache (Stage 2)
export async function saveMFR(
  editedMfrText: string,
  flowId?: string | null,
  options?: {
    expectedStage2Revision?: number;
    overspecAction?: {
      action: 'restore';
      overspecId: string;
      restoredRequirement: string;
      restorationReason: string;
    };
  },
): Promise<ApiResponse<any>> {
  if (!flowId) {
    return { success: false, error: 'flowId is required' } as any;
  }
  try {
    const response = await apiCall<any>(
      '/api/requirements/save-mfr',
      'POST',
      {
        flow_id: flowId,
        minimum_requirements: editedMfrText,
        ...(typeof options?.expectedStage2Revision === 'number'
          ? { expected_stage2_revision: options.expectedStage2Revision } : {}),
        ...(options?.overspecAction ? {
          overspec_action: {
            action: options.overspecAction.action,
            overspec_id: options.overspecAction.overspecId,
            restored_requirement: options.overspecAction.restoredRequirement,
            restoration_reason: options.overspecAction.restorationReason,
          },
        } : {}),
      }
    );
    return response as any;
  } catch (error) {
    return handleApiError(error);
  }
}

// Helper function to transform pipeline products to UI format
function transformPipelineProduct(pipelineProduct: any, index: number): Product {
  // Generate ID if not present
  const id = pipelineProduct.id || `product-${index}`;
  const specs = pipelineProduct.specifications || {};

  // Helper to safely extract string value, rejecting arrays/objects/empty strings
  const safeString = (val: any): string => {
    if (typeof val === 'string' && val.trim().length > 0) {
      return val.trim();
    }
    return '';
  };

  // Extract source from evidence
  const sourceMapping: Record<string, 'GSA' | 'Commercial' | 'Government' | 'Both'> = {
    usaspending: 'Government',
    gsa: 'GSA',
    gsa_advantage: 'GSA',
    commercial: 'Commercial'
  };
  const evidenceSource = pipelineProduct.evidence?.source?.toLowerCase() || 'commercial';
  const uiSource = sourceMapping[evidenceSource] || 'Commercial';
  
  // Note: matchScore removed as it's redundant - products already passed relevance filtering
  
  // Extract key specs from specifications object
  const keySpecs: string[] = [];
  if (pipelineProduct.specifications) {
    Object.entries(pipelineProduct.specifications).forEach(([key, value]) => {
      if (value && key !== 'award_id' && key !== 'piid') {
        if (Array.isArray(value)) {
          keySpecs.push(`${key}: ${value.join(', ')}`);
        } else if (typeof value === 'string' && value.trim()) {
          keySpecs.push(`${key}: ${value}`);
        }
      }
    });
  }
  
  // Normalize evidence for display: prefer concise labels in UI column
  const facetTagsRaw =
    pipelineProduct.facet_tags ??
    pipelineProduct.facetTags ??
    pipelineProduct.tags ??
    [];
  const facetTags: string[] = Array.isArray(facetTagsRaw)
    ? facetTagsRaw
        .map((tag: any) => (typeof tag === 'string' ? tag.trim() : ''))
        .filter((tag: string) => tag.length > 0)
    : [];

  const normalizedEvidence = ((): any => {
    const ev = pipelineProduct.evidence ? { ...pipelineProduct.evidence } : { source: evidenceSource };
    if (typeof ev.source === 'string') {
      const s = ev.source.toLowerCase();
      if (s === 'commercial_web_search') {
        ev.source = 'web_search';
      }
    }
    if (facetTags.length > 0) {
      ev.facet_tags = facetTags;
    }
    return ev;
  })();

  // Incidental product text and generic GSA facets are not a completed 2GIT
  // check. The server attaches this pair only to verified source-check results.
  const isTwoGit = pipelineProduct.source_check_id === '2git'
    && typeof pipelineProduct.item_id === 'string'
    && pipelineProduct.item_id.trim().length > 0;

  // Normalize award/contract identifiers that show up under varying keys
  const rawAwardId =
    pipelineProduct.awardId ??
    pipelineProduct.award_id ??
    specs.award_id ??
    specs.AwardID ??
    specs.awardId ??
    pipelineProduct.metadata?.award_id;
  const awardId = rawAwardId && typeof rawAwardId === 'string' && rawAwardId.trim().length > 0
    ? rawAwardId.trim()
    : undefined;

  // Safely extract product name (reject arrays/objects)
  const productName = safeString(pipelineProduct.product_name) ||
                       safeString(pipelineProduct.name) ||
                       'Unknown Product';
  
  const isMandatorySource = pipelineProduct.is_mandatory_source === true
    && typeof pipelineProduct.source_check_id === 'string'
    && pipelineProduct.source_check_id.trim().length > 0
    && typeof pipelineProduct.item_id === 'string'
    && pipelineProduct.item_id.trim().length > 0;

  return {
    id,
    // Pipeline fields
    product_name: productName,
    manufacturer: safeString(pipelineProduct.manufacturer) || 'Unknown',
    model: safeString(pipelineProduct.model) || '',
    specifications: specs,
    evidence: normalizedEvidence,
    source_type: pipelineProduct.source_type,
    meets_requirements: pipelineProduct.meets_requirements || false,
    awardId,
    isTwoGit,
    item_id: pipelineProduct.item_id,
    source_id: pipelineProduct.source_id,
    source_check_id: pipelineProduct.source_check_id,
    isMandatorySource,
    facetTags,

    // Legacy computed fields for UI compatibility
    name: productName,  // Use the same safely extracted name
    keySpecs,
    source: uiSource,
    included: false, // Default UI state
    source_evidence: pipelineProduct.source_evidence,
    configuration: pipelineProduct.configuration,
    price_provider_id: pipelineProduct.price_provider_id,
    provider_id: pipelineProduct.provider_id,
    ...normalizeProductMetadata(pipelineProduct),
  };
}

// Stage 3: Product Discovery Functions
export interface ProductSourcePlan {
  plan_status?: string;
  items: Array<{
    item_id: string;
    /** Bounded, server-projected public label. Never derive this from browser MFR text. */
    label?: string;
    explanation?: { text: string; official_url?: string };
    salient_characteristics?: string;
    description?: string;
    deliverable_kind?: string;
    two_git_research?: {
      selected: boolean;
      selectable: boolean;
      locked: boolean;
      status: 'available' | 'operator_disabled' | 'not_eligible';
    };
    source_research?: Record<string, {
      source_id: string;
      label?: string;
      selected: boolean;
      selectable: boolean;
      locked: boolean;
      status?: string;
      official_url?: string;
      selection_reason?: string;
      catalog_ids?: string[];
    }>;
    sources: Array<{
      source_id: string;
      disposition?: string;
      priority?: number;
      basis?: string;
      official_url?: string;
      dependencies?: string[];
      optional_enabled?: boolean;
      disabled_reason?: string;
    }>;
  }>;
}

export interface ProductProgramMatch {
  title: string;
  supplier?: string;
  supplier_name?: string;
  contract_number?: string;
  document_title?: string;
  official_url?: string;
  document_id?: string;
  catalog_id?: string;
  catalog_page?: number;
  scope_note?: string;
  evidence?: {
    source?: string;
    doc_type?: string;
    document_id?: string;
    page?: number;
    url?: string;
  };
}

export interface ProductSourceResults {
  research_status?: 'completed' | 'incomplete' | 'not_run';
  items: Array<{
    item_id: string;
    status?: string;
    research_status?: 'completed' | 'incomplete' | 'not_run';
    checks: Record<string, {
      status: string;
      reason_code?: string;
      official_url?: string;
      query?: string | { text?: string; bounded?: boolean; facet?: string;
        source_version?: string; notice_types?: string[]; lookback_days?: number;
        max_records_per_type?: number; psc_filter_count?: number; naics_filter_count?: number };
      adapter_version?: string;
      checked_at?: string;
      attempts?: Array<{ status: string; reason_code?: string }>;
      attempted?: boolean;
      completed?: boolean;
      notice_count?: number;
      product_count?: number;
      program_matches?: ProductProgramMatch[];
      contract_matches?: ProductProgramMatch[];
      coverage_note?: string;
      provider_results?: Record<string, { provider_id?: string; label?: string; status: string; completed?: boolean;
        attempted?: boolean; product_count?: number; reason_code?: string; checked_at?: string; publication_date?: string }>;
    }>;
  }>;
}

export interface ProductSourceContext {
  schema_version: 1;
  scope: { component?: string; location?: string; project_type?: string; fsc?: string };
  inventory: { status: 'unknown' | 'available' | 'not_available'; detail: string; evidence_url: string };
  excess: { status: 'unknown' | 'available' | 'not_available'; detail: string; evidence_url: string };
  revision?: number;
  provenance?: 'user_reported';
}

export interface ProductSourceAssessment {
  schema_version: 1;
  evaluation_status: 'completed' | 'unavailable';
  items: Array<{
    item_id: string;
    sources: Array<{
      source_id: string;
      suitability: 'supported' | 'conditional' | 'not_supported' | 'unresolved';
      recommendation: string;
      dimensions: Partial<Record<'requirement' | 'program_scope' | 'currency' | 'price' | 'quality' | 'delivery', {
        status: 'supported' | 'not_supported' | 'unknown'; finding: string;
      }>>;
      gaps: string[];
      human_action_requirements: string[];
      citations?: Array<{ title: string; url: string; page?: number; date?: string }>;
    }>;
    inventory?: { status: 'unknown' | 'recorded'; finding: string };
    excess?: { status: 'unknown' | 'recorded'; finding: string };
  }>;
}

export interface ProductDiscoverySearchResult {
  products: Product[];
  source_plan?: ProductSourcePlan;
  source_results?: ProductSourceResults;
  source_context?: ProductSourceContext;
  source_assessment?: ProductSourceAssessment;
  source_assessment_stale?: boolean;
  discovery_status?: 'completed' | 'clarification_needed' | 'review_required';
}

export interface ProductSourceRetryResult {
  item_id: string;
  source_id: string;
  check: ProductSourceResults['items'][number]['checks'][string];
  products: Product[];
}

export async function retryProductSourceCheck(
  flowId: string, itemId: string, sourceId: string, clientRequestId: string,
): Promise<ApiResponse<ProductSourceRetryResult>> {
  if (![flowId, itemId, sourceId, clientRequestId].every((value) => /^[A-Za-z0-9_-]{1,128}$/.test(value))) {
    return { success: false, error: 'Invalid source check request.', timestamp: new Date().toISOString() };
  }
  const response = await apiCall<ProductSourceRetryResult>(
    `/api/flows/${flowId}/stage/3/sources/${itemId}/${sourceId}/retry`,
    'POST', { client_request_id: clientRequestId }, { retries: 0, timeoutMs: 60_000 },
  );
  if (!response.success || !response.data) return { ...response, timestamp: new Date().toISOString() };
  return { ...response, data: {
    ...response.data,
    products: (response.data.products || []).map(transformPipelineProduct),
  }, timestamp: new Date().toISOString() };
}

export async function findMatchingProducts(
  mfr: MFR,
  flowId?: string | null,
  forceRecompute?: boolean,
  searchSources?: { catalog?: boolean; gsa?: boolean; usaspending?: boolean; webSearch?: boolean; sam?: boolean;
    selected_2git_research_item_ids?: string[]; optional_2git_item_ids?: string[];
    selected_source_research_item_ids?: Record<string, string[]>; research_selection_schema_version?: 2 },
  manualCodes?: { psc: string[]; naics: string[] }
): Promise<ApiResponse<ProductDiscoverySearchResult>> {
  console.log('Mock settings - USE_MOCK_DATA:', USE_MOCK_DATA, 'MOCK_UP_TO_STAGE:', MOCK_UP_TO_STAGE);
  console.log('shouldUseMock(3):', shouldUseMock(3));
  
  // Try to use real API first
  if (!shouldUseMock(3)) {
    try {
      console.log('Calling real API for product search...');
      // Normalize required MFR fields to satisfy backend Pydantic model
      const normalizedRequirements = {
        functional: mfr.requirements?.functional || [],
        performance: mfr.requirements?.performance || [],
        environmental: mfr.requirements?.environmental || [],
        integration: mfr.requirements?.integration || [],
        other: mfr.requirements?.other || [],
      };

      const payload = {
        // Backend expects FastAPI model MFR (see backend/models.py)
        id: mfr.id, // Use stable ID from Stage 2
        commodityDescription: mfr.originalCommodityDescription || mfr.commodityDescription || '',
        requirements: normalizedRequirements,
        generatedAt: mfr.generatedAt, // Do not default to now to keep hash stable
        version: (mfr as any).version || '1.0',
        questionsAnswered: mfr.questionsAnswered || [],
        rawText: mfr.rawText || '',
        originalCommodityDescription: mfr.originalCommodityDescription || mfr.commodityDescription || '',
        ...(flowId ? { flow_id: flowId } : {}),
        force_recompute: !!forceRecompute,
        search_sources: {
          catalog: searchSources?.research_selection_schema_version === 2
            ? catalogResearchSelected(searchSources.selected_source_research_item_ids)
            : searchSources?.catalog ?? true,
          ...(searchSources?.research_selection_schema_version !== undefined
            ? { research_selection_schema_version: searchSources.research_selection_schema_version } : {}),
          gsa: searchSources?.gsa ?? true,
          usaspending: searchSources?.usaspending ?? true,
          webSearch: searchSources?.webSearch ?? true,
          sam: searchSources?.sam ?? false,
          ...((searchSources?.selected_2git_research_item_ids ?? searchSources?.optional_2git_item_ids) !== undefined
            ? { selected_2git_research_item_ids: searchSources?.selected_2git_research_item_ids
              ?? searchSources?.optional_2git_item_ids } : {}),
          ...(searchSources?.selected_source_research_item_ids !== undefined
            ? { selected_source_research_item_ids: searchSources.selected_source_research_item_ids } : {}),
        },
        manual_codes: manualCodes || { psc: [], naics: [] }
      } as any;
      console.log('Stage3 payload keys:', Object.keys(payload), 'rawText.len:', (payload.rawText||'').length);
      const response = await apiCall<any>(
        '/api/products/search',
        'POST',
        payload,
        // Product discovery can run close to the backend's 20m timeout
        // (script wait: 1200s). Give client a bit more headroom to avoid
        // AbortError while the server continues and saves to cache.
        { retries: 0, timeoutMs: 25 * 60 * 1000 }
      );
      if (response.success) {
        // Transform pipeline format to UI format
        const pipelineProducts = response.data?.products || response.data || [];
        // Optionally: source_stats available at response.data?.source_stats
        console.log('Pipeline products length:', pipelineProducts.length);
        
        // Preserve the source check plan even when no products match.
        const transformedProducts: Product[] = Array.isArray(pipelineProducts)
          ? pipelineProducts.map(transformPipelineProduct)
          : [];

        return {
          success: true,
          data: {
            products: transformedProducts,
            source_plan: response.data?.source_plan,
            source_results: response.data?.source_results,
            discovery_status: response.data?.discovery_status,
          },
          timestamp: new Date().toISOString()
        };
      } else {
        console.log('API returned success: false');
        if (response.error === 'salient_characteristics_required' || response.error_code === 'salient_characteristics_required') {
          return {
            success: false, data: { products: [] }, error_code: 'salient_characteristics_required',
            error: 'Complete and save the salient characteristics in Stage 2, then return to Product Discovery.',
            timestamp: new Date().toISOString(),
          };
        }
        // If API explicitly returns success: false, don't fall back to mock
        return {
          success: false,
          data: { products: [] },
          error: response.error || 'Product search failed',
          timestamp: new Date().toISOString()
        };
      }
    } catch (error) {
      console.error('API call failed:', error);
      // Return error response instead of falling back to mock
      return {
        success: false,
        data: { products: [] },
        error: 'Failed to search for products. Please try again.',
        timestamp: new Date().toISOString()
      };
    }
  } else {
    // Only use mock data when explicitly configured to do so
    console.log('Using mock data for Stage 3');
    await delay(2500); // Simulate search
    
    // Load mock products from JSON file
    console.log('Loading mock products from JSON file...');
    const mockProductsModule = await import('../mockData/products-output.json');
    const mockProductsData = mockProductsModule.default || mockProductsModule;
    
    // Transform the products from the JSON file
    const pipelineProducts = mockProductsData.products || [];
    const products: Product[] = pipelineProducts.map(transformPipelineProduct);
    
    return {
      success: true,
      data: { products },
      timestamp: new Date().toISOString(),
    };
  }
}

// Stage status check
export async function getStageStatus(
  flowId: string,
  stageNum: number
): Promise<{ status: string; started_at: string | null }> {
  const resp = await apiCall<any>(`/api/flows/${flowId}/stage/${stageNum}/status`, 'GET');
  // Backend returns raw shape: { status, started_at }
  if (resp && (resp as any).status) {
    return resp as any;
  }
  if ((resp as any)?.success && (resp as any)?.data) {
    return (resp as any).data;
  }
  return { status: 'idle', started_at: null };
}

// Stage progress (polling)
export async function getStageProgress(
  flowId: string,
  stageNum: number
): Promise<{
  messages: { timestamp: string; message: string }[];
  done: boolean;
  error?: { message?: string } | string;
  error_code?: string;
} | null> {
  // Use apiCall (returns raw JSON body), but normalize to a simple shape.
  // Append a cache-busting query param. apiCall does not set Cache-Control
  // headers, and browsers / intermediate proxies can aggressively cache GETs
  // on the same URL — which would freeze the polled progress on whichever
  // snapshot was first served and prevent the spinner from advancing as the
  // backend progresses through stage phases.
  const cacheBust = `_t=${Date.now()}`;
  const resp = await apiCall<any>(`/api/flows/${flowId}/stage/${stageNum}/progress?${cacheBust}`, 'GET');
  // Backend returns raw shape: { messages, done }
  if (resp && (resp as any).messages) {
    return resp as any;
  }
  // If someone wraps it in ApiResponse later, handle that too
  if ((resp as any)?.success && (resp as any)?.data) {
    return (resp as any).data;
  }
  return null;
}

export async function resetStageProgress(flowId: string, stageNum: number): Promise<boolean> {
  const resp = await apiCall(`/api/flows/${flowId}/stage/${stageNum}/progress/reset`, 'POST', {});
  if (resp && typeof resp.success === 'boolean') {
    return resp.success;
  }
  return false;
}

// Stage 4: PSC Code Assignment Functions
export async function assignPSCCodes(
  products: Product[],
  flowId?: string | null,
  forceRecompute: boolean = false,
): Promise<ApiResponse<ProductWithPSC[]>> {
  // Explicit dev-mode opt-in: load mock fixture for offline testing only.
  if (shouldUseMock(4)) {
    await delay(1500); // Simulate processing

    // Use the actual products_with_psc from the JSON file directly
    const productsWithPSC = mockPscOutput.products_with_psc as any[];

    return {
      success: true,
      data: productsWithPSC,
      timestamp: new Date().toISOString(),
    };
  }

  // Production path — no silent mock fallback. Surface backend errors to the UI.
  const response = await apiCall<ProductWithPSC[]>(
    '/api/psc/assign',
    'POST',
    { products, ...(flowId ? { flow_id: flowId } : {}), force_recompute: !!forceRecompute }
  );

  if (response.success) {
    return {
      ...response,
      timestamp: new Date().toISOString()
    };
  }

  const err = new Error(response.error ?? USER_FACING_ERROR_MESSAGES.wait_and_retry);
  (err as any).error_code = response.error_code;
  (err as any).technical_detail = response.technical_detail;
  throw err;
}

export interface ProductSelectionBrandNameInput {
  schema_version: 1;
  requested: boolean;
  scope: 'entire' | 'portion' | null;
  covered_product_keys: string[];
  manual_psc_codes: string[];
  manual_naics_codes: string[];
  source_revision: number;
  idempotency_key: string;
}

export interface ProductSelectionBrandNameRecord extends ProductSelectionBrandNameInput {
  post_ja_destination_stage: 4 | 5 | 6;
  decision_revision: number;
  decision_updated_at: string;
}

export interface ProductSelectionResponse {
  selected_ids: string[];
  selected_keys: string[];
  brand_name?: ProductSelectionBrandNameRecord | null;
  next_stage: 4 | 5 | 6 | 14;
}

export type ProductSelectionApiResponse = TransportApiResponse<ProductSelectionResponse> & {
  timestamp: string;
};

/** Stable Product Discovery identity shared by the selection and J&A evidence. */
export function productSelectionKey(product: Product): string {
  const normalize = (value: unknown) => (
    typeof value === 'string'
      ? value.trim().toLowerCase()
      : String(value || '').trim().toLowerCase()
  );
  const specifications = product.specifications || {};
  const awardIdentity = normalize((specifications as any).piid || (specifications as any).award_id || '');
  return `${normalize(product.product_name || product.name)}|${normalize(product.manufacturer)}|${normalize(product.model)}|${awardIdentity}`;
}

// Persist selected product IDs and the optional atomic Stage-3 brand decision.
export async function saveSelectedProducts(
  flowId: string,
  selectedProducts: Product[],
  brandName?: ProductSelectionBrandNameInput,
  options?: { confirmBrandNameDeactivation?: boolean },
): Promise<ProductSelectionApiResponse> {
  try {
    const ids = selectedProducts.map(p => String(p.id));
    const keys = selectedProducts.map(productSelectionKey);
    const resp = await apiCall<ProductSelectionResponse>(
      '/api/products/selection',
      'POST',
      {
        ...(flowId ? { flow_id: flowId } : {}),
        selected_ids: ids,
        selected_keys: keys,
        ...(brandName ? { brand_name: brandName } : {}),
        ...(options?.confirmBrandNameDeactivation
          ? { confirm_brand_name_deactivation: true }
          : {}),
      }
    );
    return { ...resp, timestamp: new Date().toISOString() };
  } catch (error) {
    const categorized = error as Error & {
      error_code?: string;
      technical_detail?: string;
      required_stage?: number;
      requested_stage?: number;
      feature?: string;
    };
    return {
      success: false,
      error: categorized.message || USER_FACING_ERROR_MESSAGES.wait_and_retry,
      ...(categorized.error_code ? { error_code: categorized.error_code } : {}),
      ...(categorized.technical_detail ? { technical_detail: categorized.technical_detail } : {}),
      ...(categorized.required_stage !== undefined ? { required_stage: categorized.required_stage } : {}),
      ...(categorized.requested_stage !== undefined ? { requested_stage: categorized.requested_stage } : {}),
      ...(categorized.feature ? { feature: categorized.feature } : {}),
      timestamp: new Date().toISOString(),
    };
  }
}

// Save edited PSC assignment to cache (Stage 4)
export async function savePSCAssignment(
  productsWithPSC: any[],
  flowId?: string | null,
  methodUsed: 'ai' | 'manual' = 'manual',
): Promise<ApiResponse<any[]>> {
  if (!flowId) {
    return { success: false, error: 'flowId is required' } as any;
  }
  try {
    const response = await apiCall<any[]>(
      '/api/psc/save',
      'POST',
      { flow_id: flowId, products_with_psc: productsWithPSC, method_used: methodUsed }
    );
    return response as any;
  } catch (error) {
    return handleApiError(error);
  }
}

// Stage 5: NAICS Code Mapping Functions
export async function mapNAICSCodes(
  products: ProductWithPSC[],
  flowId?: string | null,
  forceRecompute: boolean = false
): Promise<ApiResponse<{ products_with_naics: ProductWithCodes[]; method_used?: 'ai' | 'manual' }>> {
  // Explicit dev-mode opt-in: load mock fixture for offline testing only.
  if (shouldUseMock(5)) {
    await delay(1500); // Simulate processing

    // Load the actual script output sample
    const mockNaicsOutput = await import('../mockData/naics-output.json');

    // Merge the products with PSC from previous stage with NAICS data
    const productsWithCodes: ProductWithCodes[] = products.map((product, index) => {
      // Get corresponding product from mock output if available
      const mockProduct = mockNaicsOutput.products_with_naics[index] || mockNaicsOutput.products_with_naics[0];

      return {
        ...product,
        // Preserve all fields from the product (including PSC codes from stage 3)
        // Add fields from the actual script output
        ...mockProduct,
        // Ensure ID is preserved
        id: product.id,
        // Script output structure
        naics_from_psc: mockProduct.naics_from_psc || [],
        psc_codes_api: mockProduct.psc_codes_api || [],
        // Legacy fields for UI compatibility (computed from script output)
        naicsCode: mockProduct.naics_from_psc?.[0] || '',
        naicsDescription: `NAICS ${mockProduct.naics_from_psc?.[0] || 'N/A'}`,
      };
    });

    return {
      success: true,
      data: { products_with_naics: productsWithCodes, method_used: 'ai' },
      timestamp: new Date().toISOString(),
    };
  }

  // Production path — no silent mock fallback. Surface backend errors to the UI.
  const response = await apiCall<ProductWithCodes[] | { products_with_naics?: ProductWithCodes[]; products?: ProductWithCodes[]; method_used?: 'ai' | 'manual' }>(
    '/api/naics/map',
    'POST',
    { products, ...(flowId ? { flow_id: flowId } : {}), force_recompute: !!forceRecompute }  // Send as object with products array
  );

  // Transform the response to ensure UI compatibility
  // The script returns naics_from_psc as array of strings
  if (response.success && response.data) {
    const responseData: any = response.data;
    const rawProducts: ProductWithCodes[] = Array.isArray(responseData)
      ? responseData
      : responseData.products_with_naics || responseData.products || [];
    const methodUsed = responseData.method_used === 'ai' || responseData.method_used === 'manual'
      ? responseData.method_used
      : (response as any).method_used === 'ai' || (response as any).method_used === 'manual'
      ? (response as any).method_used
      : undefined;
    const transformedData = rawProducts.map(product => {
      const primaryNAICS = product.naics_from_psc?.[0] || product.naicsCode || '';
      const naicsWithDesc = product.naics_with_descriptions || [];
      const naicsMatch = naicsWithDesc.find((item: any) => item.code === primaryNAICS);
      const description = naicsMatch?.description || product.naicsDescription || '';

      return {
        ...product,
        // Add legacy fields for UI if needed
        naicsCode: primaryNAICS,
        naicsDescription: description,
        naicsConfidence: product.naicsConfidence || 90,
        alternativeNAICS: product.alternativeNAICS || [],
        historicalUsage: product.historicalUsage || 0,
      };
    });

    return {
      success: true,
      data: { products_with_naics: transformedData, ...(methodUsed ? { method_used: methodUsed } : {}) },
      timestamp: new Date().toISOString(),
    };
  }

  const err = new Error(response.error ?? USER_FACING_ERROR_MESSAGES.wait_and_retry);
  (err as any).error_code = response.error_code;
  (err as any).technical_detail = response.technical_detail;
  throw err;
}

// Save Stage 5 NAICS selection (persist user's primary NAICS choices)
export async function saveNAICSSelection(
  productsWithCodes: ProductWithCodes[],
  flowId?: string | null,
  methodUsed?: 'ai' | 'manual' | 'unknown',
): Promise<ApiResponse<any>> {
  if (!flowId) {
    return { success: false, error: 'flowId is required' } as any;
  }
  try {
    const response = await apiCall<any>(
      '/api/naics/save-selection',
      'POST',
      {
        flow_id: flowId,
        products_with_naics: productsWithCodes,
        ...(methodUsed === 'ai' || methodUsed === 'manual' ? { method_used: methodUsed } : {}),
      }
    );
    return response as any;
  } catch (error) {
    return handleApiError(error);
  }
}

// Stage 6: Vendor Discovery Functions
export type VendorDiscoveryJobState = 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';

export interface VendorDiscoveryJob {
  job_id: string;
  state: VendorDiscoveryJobState;
  status?: string;
  progress?: Record<string, unknown>;
  status_url?: string;
  result_reference?: Record<string, unknown>;
  queue_position?: number;
  created_at?: string;
  started_at?: string;
  heartbeat_at?: string;
  completed_at?: string;
  /** Mock/development compatibility only; production exposes result_reference. */
  result?: unknown;
}

/**
 * `apiCall` models the wire envelope, whose timestamp is optional because
 * older endpoints do not return one. Stage processors consume the pipeline
 * envelope, which requires a timestamp for state/history handling. Normalize
 * the transport response at this boundary rather than weakening either type.
 */
const asPipelineApiResponse = <T>(response: TransportApiResponse<T>): ApiResponse<T> => ({
  ...response,
  timestamp: new Date().toISOString(),
});

const createVendorDiscoveryIdempotencyKey = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `vendor-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

/**
 * Submit a durable Stage 6 job. This intentionally does not use the generic
 * transport retry policy: submission is idempotent at the server and the UI
 * reconnects to the returned job instead of creating overlapping searches.
 */
export async function searchVendors(
  products: ProductWithCodes[],
  flowId?: string | null,
  forceRecompute: boolean = false,
  recommendedVendors: RecommendedVendorInput[] = [],
  idempotencyKey: string = createVendorDiscoveryIdempotencyKey(),
): Promise<ApiResponse<VendorDiscoveryJob>> {
  if (!products || products.length === 0) {
    console.warn('[stageFunctions] searchVendors called with no products; skipping API call');
    return {
      success: true,
      data: undefined,
      timestamp: new Date().toISOString(),
    };
  }

  // Explicit dev-mode opt-in: load mock fixture for offline testing only.
  if (shouldUseMock(6)) {
    await delay(1500); // Simulate processing

    // Load the actual script output sample
    const mockVendorsOutput = await import('../mockData/vendors-output.json');

    // Merge the products with vendor data
    const productsWithVendors = products.map((product, index) => {
      // Get corresponding product from mock output if available
      const mockProduct = mockVendorsOutput.products_with_vendors[index] ||
                         mockVendorsOutput.products_with_vendors[0];

      return {
        ...product,
        // Add vendor array from the script output
        vendors: mockProduct.vendors || []
      };
    });

    return {
      success: true,
      data: {
        job_id: 'mock-vendor-discovery',
        state: 'completed',
        status: 'Vendor discovery completed',
        // The production contract uses a result reference. Mock mode is only
        // retained for offline development and does not run the job API.
        result_reference: {
          flow_id: flowId ?? 'mock-vendor-discovery',
          stage: 6,
          output: 'stage6',
        },
        result: { products_with_vendors: productsWithVendors },
      },
      timestamp: new Date().toISOString(),
    };
  }

  // Production path — no silent mock fallback. Surface backend errors to the UI.
  const response = await apiCall<any>(
    '/api/vendors/search',
    'POST',
    {
      products,
      ...(flowId ? { flow_id: flowId } : {}),
      force_recompute: !!forceRecompute,
      recommended_vendors: recommendedVendors,
    },
    {
      retries: 0,
      headers: { 'Idempotency-Key': idempotencyKey },
    },
  );

  if (response.success && response.data) {
    return {
      success: true,
      data: response.data as VendorDiscoveryJob,
      timestamp: new Date().toISOString(),
    };
  }

  const err = new Error(response.error ?? USER_FACING_ERROR_MESSAGES.wait_and_retry);
  (err as any).error_code = response.error_code;
  (err as any).technical_detail = response.technical_detail;
  throw err;
}

/** Fetch the sanitized durable-job view used for Stage 6 reconnect/polling. */
export async function getVendorDiscoveryJob(
  jobId: string,
): Promise<ApiResponse<VendorDiscoveryJob>> {
  const response = await apiCall<VendorDiscoveryJob>(
    `/api/vendors/jobs/${encodeURIComponent(jobId)}`,
    'GET',
    undefined,
    { retries: 0 },
  );
  if (!response.success) {
    const err = new Error(response.error ?? USER_FACING_ERROR_MESSAGES.wait_and_retry);
    (err as any).error_code = response.error_code;
    throw err;
  }
  return asPipelineApiResponse(response);
}

/** Cancel is explicit; navigating away must never cancel a durable job. */
export async function cancelVendorDiscoveryJob(
  jobId: string,
): Promise<ApiResponse<VendorDiscoveryJob>> {
  const response = await apiCall<VendorDiscoveryJob>(
    `/api/vendors/jobs/${encodeURIComponent(jobId)}/cancel`,
    'POST',
    undefined,
    { retries: 0 },
  );
  return asPipelineApiResponse(response);
}

/**
 * Persist an explicit selection from the bounded Stage-6 considered-candidate
 * list. The server resolves the stable key against its cached record; vendor
 * details are never supplied by the browser.
 */
export async function saveConsideredVendorSelection(
  flowId: string,
  productId: string,
  stableKey: string,
  selected: boolean,
): Promise<ProductWithVendors> {
  const response = await apiCall<any>(
    '/api/vendors/considered-selection',
    'POST',
    {
      flow_id: flowId,
      product_id: productId,
      stable_key: stableKey,
      selected,
    },
  );
  if (response.success && response.data) {
    return normalizeProductsWithVendors([response.data])[0];
  }
  const err = new Error(response.error ?? USER_FACING_ERROR_MESSAGES.wait_and_retry);
  (err as any).error_code = response.error_code;
  throw err;
}

export type VendorMrrProcessInformation = Partial<Record<
  'large_business' | 'educational' | 'non_profit' | 'small_business' | 'government' | 'other',
  string
>>;

export type SectionEContactCounts = Partial<Record<keyof VendorMrrProcessInformation, number>>;
export interface SectionEContactedSource {
  name: string;
  cage_code?: string;
  uei?: string;
  category: keyof VendorMrrProcessInformation;
}
export interface SectionEContactReview {
  mention: string;
  reason: 'unmatched' | 'ambiguous';
}
export interface SectionEGenerationResponse extends ApiResponse<string> {
  contacted_source_counts?: SectionEContactCounts;
  contacted_sources?: SectionEContactedSource[];
  contacted_source_review?: SectionEContactReview[];
  unselected_vendor_mentions?: string[];
}

export interface VendorMrrSourceDetailsResponse {
  products_with_vendors?: unknown[];
  process_information?: VendorMrrProcessInformation;
}

/**
 * Persist only the Stage-6 supplier narratives and source counts that feed
 * the Streamlined MRR. Identity, contact, and discovery evidence remain
 * server-owned and are intentionally absent from this command.
 */
export async function saveVendorMrrSourceDetails(
  flowId: string,
  details: (
    { product_id: string; vendor_id: string; fields: Partial<Record<'capabilities' | 'past_performance', string>> }
    | { process_information: VendorMrrProcessInformation }
  ),
): Promise<VendorMrrSourceDetailsResponse> {
  const response = await apiCall<VendorMrrSourceDetailsResponse>(
    '/api/vendors/mrr-source-details',
    'POST',
    { flow_id: flowId, ...details },
  );
  if (response.success && response.data) return response.data;
  const err = new Error(response.error ?? USER_FACING_ERROR_MESSAGES.wait_and_retry);
  (err as any).error_code = response.error_code;
  throw err;
}

// Legacy vendor search function (deprecated - kept for backward compatibility)
export async function searchVendorsLegacy(
  products: ProductWithCodes[]
): Promise<ApiResponse<Vendor[]>> {
  await delay(3000); // Simulate vendor search
  
  const vendors: Vendor[] = [
    {
      id: 'vendor-1',
      name: 'Raytheon Technologies Corporation',
      cageCode: '49956',
      dunsNumber: '001234567',
      socioeconomicStatus: ['Large Business'],
      naicsCodes: ['334511', '334290'],
      pscCodes: ['5840', '5841'],
      pastPerformance: {
        rating: 4.8,
        contractCount: 156,
        totalValue: '$2.3B',
      },
      capabilities: [
        'Radar Systems Design',
        'Systems Integration',
        'Technical Support',
        'Training Services',
      ],
      certifications: [
        'ISO 9001:2015',
        'AS9100D',
        'CMMI Level 5',
      ],
      contactInfo: {
        email: 'gov.contracts@rtx.com',
        phone: '781-522-3000',
        address: '870 Winter Street, Waltham, MA 02451',
        website: 'https://www.rtx.com',
      },
      samRegistered: true,
      productsOffered: ['AN/SPS-48E', 'AN/SPY-1', 'SeaRAM'],
    },
    {
      id: 'vendor-2',
      name: 'Advanced Defense Systems LLC',
      cageCode: '7B4K9',
      socioeconomicStatus: ['Small Business', 'Woman-Owned Small Business'],
      naicsCodes: ['334511', '541330'],
      pscCodes: ['5840'],
      pastPerformance: {
        rating: 4.5,
        contractCount: 23,
        totalValue: '$45M',
      },
      capabilities: [
        'Radar Components',
        'Integration Services',
        'Maintenance Support',
      ],
      certifications: [
        'ISO 9001:2015',
      ],
      contactInfo: {
        email: 'contracts@advdefense.com',
        phone: '703-555-0123',
        address: '1234 Defense Way, Arlington, VA 22202',
      },
      samRegistered: true,
      productsOffered: ['Radar Components', 'Integration Services'],
    },
    {
      id: 'vendor-3',
      name: 'Tactical Systems International',
      cageCode: '5M8N2',
      socioeconomicStatus: ['Small Business', 'Service-Disabled Veteran-Owned'],
      naicsCodes: ['334511'],
      pscCodes: ['5840', '5845'],
      pastPerformance: {
        rating: 4.3,
        contractCount: 18,
        totalValue: '$28M',
      },
      capabilities: [
        'System Maintenance',
        'Technical Documentation',
        'Field Support',
      ],
      certifications: [
        'ISO 9001:2015',
        'ISO 27001',
      ],
      contactInfo: {
        email: 'info@tacticalsys.com',
        phone: '571-555-0456',
        address: '5678 Military Road, Falls Church, VA 22041',
      },
      samRegistered: true,
      productsOffered: ['Maintenance Services', 'Technical Support'],
    },
  ];
  
  return {
    success: true,
    data: vendors,
    timestamp: new Date().toISOString(),
  };
}

// Stage 7: Section B Generation Functions
export async function generateSectionB(
  productsWithVendors: ProductWithVendors[],
  flowId?: string | null,
  forceRecompute: boolean = false
): Promise<ApiResponse<string> & { unselected_vendor_mentions?: string[]; vendor_selection_stale?: boolean }> {
  // Explicit dev-mode opt-in: use synthetic Markdown for offline testing only.
  if (shouldUseMock(7)) {
    await delay(1500); // Simulate processing

    const fallbackContent = `# SECTION B - SUPPLIES OR SERVICES AND PRICES/COSTS

## Contract Line Items

| CLIN | Item Description | Quantity | Unit | Unit Price | Total Price |
|------|-----------------|----------|------|------------|-------------|
| 0001 | Trimble R12i GNSS System | 1 | EA | TBD | TBD |
| 0002 | Surveying Equipment | 1 | EA | TBD | TBD |
| 0003 | TRIMBLE 12I GNSS SYSTEM | 1 | EA | TBD | TBD |

## Notes
- Prices to be determined through competitive bidding
- Delivery: 90 days ARO
- Contract Type: FFP (Firm Fixed Price)

*Generated from vendor discovery data*`;

    return {
      success: true,
      data: fallbackContent,
      timestamp: new Date().toISOString(),
    };
  }

  // Production path — no silent mock fallback. Surface backend errors to the UI.
  const response = await apiCall<string>(
    '/api/section-b/generate',
    'POST',
    {
      ...(flowId ? { flow_id: flowId } : {}),
      products_with_vendors: productsWithVendors,
      force_recompute: forceRecompute,
      // Include only stable, optional fields when specified by UI. Defaults handled server-side.
      // quantities, delivery_date, contract_type can be added here when UI supports edits.
    }
  );

  if (response.success && response.data) {
    return {
      success: true,
      data: response.data,  // Section B markdown content
      timestamp: new Date().toISOString(),
      unselected_vendor_mentions: (response as typeof response & { unselected_vendor_mentions?: string[] }).unselected_vendor_mentions,
      vendor_selection_stale: (response as typeof response & { vendor_selection_stale?: boolean }).vendor_selection_stale,
    };
  }

  // Propagate the backend error so the UI can render it.
  const err = new Error(response.error ?? USER_FACING_ERROR_MESSAGES.wait_and_retry);
  (err as any).error_code = response.error_code;
  (err as any).technical_detail = response.technical_detail;
  throw err;
}

export type CurrentStageUpdateResult = Pick<TransportApiResponse<unknown>,
  'success' | 'error' | 'error_code' | 'technical_detail' |
  'required_stage' | 'requested_stage' | 'feature'>;

// Update current stage when user navigates
export async function updateCurrentStage(
  flowId: string,
  stageNum: number
): Promise<CurrentStageUpdateResult> {
  const response = await apiCall<unknown>(
    `/api/flows/${encodeURIComponent(flowId)}/current-stage`,
    'PATCH',
    { stage_num: stageNum },
  );
  // This endpoint still returns a plain 200 message instead of the usual
  // success envelope. Recognize only its exact acknowledgement; an explicit
  // `success: false` must never be mistaken for a successful write.
  if (response.success === true || (
    response.success === undefined
    && response.message === `Current stage updated to ${stageNum}`
  )) return { success: true };
  return {
    success: false,
    ...(response.error ? { error: response.error } : {}),
    ...(response.error_code ? { error_code: response.error_code } : {}),
    ...(response.technical_detail ? { technical_detail: response.technical_detail } : {}),
    ...(response.required_stage !== undefined ? { required_stage: response.required_stage } : {}),
    ...(response.requested_stage !== undefined ? { requested_stage: response.requested_stage } : {}),
    ...(response.feature ? { feature: response.feature } : {}),
  };
}

/**
 * Generate Section C (Acquisition Requirements Documentation)
 */
/**
 * Get context questions for Section C generation
 */
export async function getSectionCQuestions(
  productsWithVendors: ProductWithVendors[],
  flowId?: string | null
): Promise<ApiResponse<any[]>> {
  try {
    const response = await apiCall<any[]>(
      '/api/section-c/questions',
      'POST',
      {
        ...(flowId ? { flow_id: flowId } : {}),
        products_with_vendors: productsWithVendors,
      }
    );

    if (response.success && response.data) {
      return {
        success: true,
        data: response.data,
        timestamp: new Date().toISOString(),
      };
    }

    const err = new Error(response.error || USER_FACING_ERROR_MESSAGES.maintainers_alerted);
    (err as any).error_code = response.error_code;
    (err as any).technical_detail = response.technical_detail;
    throw err;
  } catch (error) {
    console.error('Error generating Section C questions:', error);
    throw error;
  }
}

/**
 * Save Section C context question answers
 */
export async function saveSectionCAnswers(
  flowId: string,
  questions: any[],
  answers: Record<string, string>
): Promise<ApiResponse<any>> {
  try {
    const response = await apiCall<any>(
      '/api/section-c/save-answers',
      'POST',
      {
        ...(flowId ? { flow_id: flowId } : {}),
        questions: questions,
        answers: answers,
      }
    );

    if (response.success) {
      return {
        success: true,
        data: response.data,
        timestamp: new Date().toISOString(),
      };
    }

    const err = new Error(response.error || USER_FACING_ERROR_MESSAGES.maintainers_alerted);
    (err as any).error_code = response.error_code;
    (err as any).technical_detail = response.technical_detail;
    throw err;
  } catch (error) {
    console.error('Error saving Section C answers:', error);
    throw error;
  }
}

export async function saveSectionContent(
  flowId: string,
  section: 'b' | 'c' | 'd' | 'e',
  content: string
): Promise<ApiResponse<any>> {
  try {
    const response = await apiCall<any>(
      `/api/section-${section}/save-content`,
      'POST',
      {
        flow_id: flowId,
        content: content,
      }
    );

    if (response.success) {
      return {
        success: true,
        data: response.data,
        timestamp: new Date().toISOString(),
      };
    }

    const err = new Error(response.error || USER_FACING_ERROR_MESSAGES.maintainers_alerted);
    (err as any).error_code = response.error_code;
    (err as any).technical_detail = response.technical_detail;
    throw err;
  } catch (error) {
    console.error(`Error saving Section ${section.toUpperCase()} content:`, error);
    throw error;
  }
}

/** Save the native MRR Other-method checkbox/detail with its Section D source. */
export async function saveSectionDOtherMethod(
  flowId: string,
  otherSelected: boolean,
  otherDetail: string
): Promise<ApiResponse<{ other_selected: boolean; other_detail: string }>> {
  const response = await apiCall<{ other_selected: boolean; other_detail: string }>(
    `/api/section-d/${encodeURIComponent(flowId)}/other-method`,
    'POST',
    { other_selected: otherSelected, other_detail: otherDetail }
  );
  if (response.success && response.data) {
    return { success: true, data: response.data, timestamp: new Date().toISOString() };
  }
  const err = new Error(response.error || USER_FACING_ERROR_MESSAGES.maintainers_alerted);
  (err as any).error_code = response.error_code;
  (err as any).technical_detail = response.technical_detail;
  throw err;
}

/**
 * Record the authenticated CO/CS's explicit D/E Continue action.
 *
 * The server validates the persisted final document and creates the durable
 * actor/timestamp fact used by the RA's content-free progress viewer.  The
 * browser never supplies an identity or completion timestamp.
 */
export async function completeMarketResearchContractingStage(
  flowId: string,
  stage: 9 | 10,
): Promise<ApiResponse<{ completed: { by_email: string; at: string } }>> {
  const response = await apiCall<{ completed: { by_email: string; at: string } }>(
    `/api/market-research/flows/${encodeURIComponent(flowId)}/contracting-stages/${stage}/complete`,
    'POST',
    {},
  );
  if (response.success && response.data) {
    return {
      success: true,
      data: response.data,
      timestamp: new Date().toISOString(),
    };
  }
  const err = new Error(response.error || USER_FACING_ERROR_MESSAGES.maintainers_alerted);
  (err as any).error_code = response.error_code;
  (err as any).technical_detail = response.technical_detail;
  throw err;
}

export async function generateSectionC(
  productsWithVendors: ProductWithVendors[],
  flowId?: string | null,
  forceRecompute: boolean = false,
  contextAnswers?: Record<string, string>
): Promise<ApiResponse<string> & { unselected_vendor_mentions?: string[]; vendor_selection_stale?: boolean }> {
  if (!shouldUseMock(8)) {
    try {
      const respAny: any = await apiCall<any>(
        "/api/section-c/generate",
        "POST",
        {
          ...(flowId ? { flow_id: flowId } : {}),
          products_with_vendors: productsWithVendors,
          force_recompute: forceRecompute,
          context_answers: contextAnswers,
        }
      );

      // Accept multiple backend shapes: ApiResponse, plain string, or UI-style keys
      let content: string | undefined;
      const resp = respAny; // widen to allow alternative shapes
      if (typeof resp === 'string') {
        content = resp;
      } else if (resp && typeof resp === 'object') {
        // Prefer explicit data field when it's a string
        if (typeof resp.data === 'string') {
          content = resp.data;
        } else {
          content =
            resp.sectionCContent ||
            resp.section_c_content ||
            resp.section_c ||
            (resp.data && (resp.data.sectionCContent || resp.data.section_c_content || resp.data.section_c));
        }
      }

      if (typeof content === 'string' && content.trim().length > 0) {
        return {
          success: true,
          data: content,
          timestamp: new Date().toISOString(),
          unselected_vendor_mentions: resp?.unselected_vendor_mentions,
          vendor_selection_stale: resp?.vendor_selection_stale,
        };
      }

      // If API responded with an explicit error shape
      if (resp && typeof resp === 'object' && resp.success === false && resp.error) {
        const err = new Error(resp.error);
        (err as any).error_code = resp.error_code;
        (err as any).technical_detail = resp.technical_detail;
        throw err;
      }

      throw new Error('Failed to generate Section C.');
    } catch (error) {
      console.error('Error generating Section C:', error);
      throw error;
    }
  }

  // Should not be reached with stage 8 mock disabled; keep explicit message if it is.
  throw new Error('Mock mode is enabled for Stage 8 but no mock implementation exists.');
}

/**
 * Get context questions for Section D generation
 */
type SectionDQuestionsContext = {
  answers?: Record<string, string>;
  questions_research_evidence_stale?: boolean;
};

export async function getSectionDQuestions(
  productsWithVendors: ProductWithVendors[],
  flowId?: string | null,
  forceRecompute: boolean = false
): Promise<ApiResponse<any[]> & SectionDQuestionsContext> {
  try {
    const response: TransportApiResponse<any[]> & SectionDQuestionsContext = await apiCall<any[]>(
      '/api/section-d/questions',
      'POST',
      {
        ...(flowId ? { flow_id: flowId } : {}),
        products_with_vendors: productsWithVendors,
        force_recompute: forceRecompute,
      }
    );

    if (response.success && response.data) {
      return {
        success: true,
        data: response.data,
        answers: response.answers,
        questions_research_evidence_stale: response.questions_research_evidence_stale,
        timestamp: new Date().toISOString(),
      };
    }

    const err = new Error(response.error || USER_FACING_ERROR_MESSAGES.maintainers_alerted);
    (err as any).error_code = response.error_code;
    (err as any).technical_detail = response.technical_detail;
    throw err;
  } catch (error) {
    console.error('Error generating Section D questions:', error);
    throw error;
  }
}

/**
 * Save Section D context question answers
 */
export async function saveSectionDAnswers(
  flowId: string,
  questions: any[],
  answers: Record<string, string>
): Promise<ApiResponse<any>> {
  try {
    const response = await apiCall<any>(
      '/api/section-d/save-answers',
      'POST',
      {
        ...(flowId ? { flow_id: flowId } : {}),
        questions: questions,
        answers: answers,
      }
    );

    if (response.success) {
      return {
        success: true,
        data: response.data,
        timestamp: new Date().toISOString(),
      };
    }

    const err = new Error(response.error || USER_FACING_ERROR_MESSAGES.maintainers_alerted);
    (err as any).error_code = response.error_code;
    (err as any).technical_detail = response.technical_detail;
    throw err;
  } catch (error) {
    console.error('Error saving Section D answers:', error);
    throw error;
  }
}

/**
 * Generate Section D (Packaging and Marking)
 */
export async function generateSectionD(
  productsWithVendors: ProductWithVendors[],
  flowId?: string | null,
  forceRecompute: boolean = false,
  contextAnswers?: Record<string, string>
): Promise<ApiResponse<string> & { unselected_vendor_mentions?: string[]; vendor_selection_stale?: boolean; research_evidence_stale?: boolean; research_method_account?: string }> {
  if (!shouldUseMock(9)) {
    try {
      const respAny: any = await apiCall<any>(
        "/api/section-d/generate",
        "POST",
        {
          ...(flowId ? { flow_id: flowId } : {}),
          products_with_vendors: productsWithVendors,
          force_recompute: forceRecompute,
          context_answers: contextAnswers,
        }
      );

      // Accept multiple backend shapes: ApiResponse, plain string, or UI-style keys
      let content: string | undefined;
      const resp = respAny; // widen to allow alternative shapes
      if (typeof resp === 'string') {
        content = resp;
      } else if (resp && typeof resp === 'object') {
        // Prefer explicit data field when it's a string
        if (typeof resp.data === 'string') {
          content = resp.data;
        } else {
          content =
            resp.sectionDContent ||
            resp.section_d_content ||
            resp.section_d ||
            (resp.data && (resp.data.sectionDContent || resp.data.section_d_content || resp.data.section_d));
        }
      }

      if (typeof content === 'string' && content.trim().length > 0) {
        return {
          success: true,
          data: content,
          timestamp: new Date().toISOString(),
          unselected_vendor_mentions: resp?.unselected_vendor_mentions,
          vendor_selection_stale: resp?.vendor_selection_stale,
          research_evidence_stale: resp?.research_evidence_stale,
          research_method_account: resp?.research_method_account,
        };
      }

      // If API responded with an explicit error shape
      if (resp && typeof resp === 'object' && resp.success === false && resp.error) {
        const err = new Error(resp.error);
        (err as any).error_code = resp.error_code;
        (err as any).technical_detail = resp.technical_detail;
        throw err;
      }

      throw new Error('Failed to generate Section D.');
    } catch (error) {
      console.error('Error generating Section D:', error);
      throw error;
    }
  }

  // Should not be reached with stage 9 mock disabled; keep explicit message if it is.
  throw new Error('Mock mode is enabled for Stage 9 but no mock implementation exists.');
}

// =============================================================================
// Section E Generation
// =============================================================================

// 1. Get context questions
export async function getSectionEQuestions(
  productsWithVendors: ProductWithVendors[],
  flowId?: string | null
): Promise<ApiResponse<any[]>> {
  try {
    const response = await apiCall<any[]>(
      '/api/section-e/questions',
      'POST',
      {
        ...(flowId ? { flow_id: flowId } : {}),
        products_with_vendors: productsWithVendors,
      }
    );

    if (response.success && response.data) {
      return {
        success: true,
        data: response.data,
        timestamp: new Date().toISOString(),
      };
    }

    const err = new Error(response.error || USER_FACING_ERROR_MESSAGES.maintainers_alerted);
    (err as any).error_code = response.error_code;
    (err as any).technical_detail = response.technical_detail;
    throw err;
  } catch (error) {
    console.error('Error generating Section E questions:', error);
    throw error;
  }
}

// 2. Save context answers
export async function saveSectionEAnswers(
  flowId: string,
  questions: any[],
  answers: Record<string, string>
): Promise<ApiResponse<any>> {
  try {
    const response = await apiCall<any>(
      '/api/section-e/save-answers',
      'POST',
      {
        ...(flowId ? { flow_id: flowId } : {}),
        questions: questions,
        answers: answers,
      }
    );

    if (response.success) {
      return {
        success: true,
        data: response.data,
        timestamp: new Date().toISOString(),
      };
    }

    const err = new Error(response.error || USER_FACING_ERROR_MESSAGES.maintainers_alerted);
    (err as any).error_code = response.error_code;
    (err as any).technical_detail = response.technical_detail;
    throw err;
  } catch (error) {
    console.error('Error saving Section E answers:', error);
    throw error;
  }
}

// 3. Generate section content
export async function generateSectionE(
  productsWithVendors: ProductWithVendors[],
  flowId?: string | null,
  forceRecompute: boolean = false,
  contextAnswers?: Record<string, string>
): Promise<SectionEGenerationResponse> {
  if (!shouldUseMock(10)) {  // Use stage number 10
    try {
      const respAny: any = await apiCall<any>(
        "/api/section-e/generate",
        "POST",
        {
          ...(flowId ? { flow_id: flowId } : {}),
          products_with_vendors: productsWithVendors,
          force_recompute: forceRecompute,
          context_answers: contextAnswers,
        }
      );

      // Handle multiple response formats
      let content: string | undefined;
      const resp = respAny;
      if (typeof resp === 'string') {
        content = resp;
      } else if (resp && typeof resp === 'object') {
        if (typeof resp.data === 'string') {
          content = resp.data;
        } else {
          content =
            resp.sectionEContent ||
            resp.section_e_content ||
            resp.section_e ||
            (resp.data && (resp.data.sectionEContent || resp.data.section_e_content || resp.data.section_e));
        }
      }

      if (typeof content === 'string' && content.trim().length > 0) {
        const metadata = resp && typeof resp === 'object' ? resp : {};
        return {
          success: true,
          data: content,
          timestamp: new Date().toISOString(),
          contacted_source_counts: metadata.contacted_source_counts,
          contacted_sources: metadata.contacted_sources,
          contacted_source_review: metadata.contacted_source_review,
          unselected_vendor_mentions: metadata.unselected_vendor_mentions,
        };
      }

      if (resp && typeof resp === 'object' && resp.success === false && resp.error) {
        const err = new Error(resp.error);
        (err as any).error_code = resp.error_code;
        (err as any).technical_detail = resp.technical_detail;
        throw err;
      }

      throw new Error('Failed to generate Section E.');
    } catch (error) {
      console.error('Error generating Section E:', error);
      throw error;
    }
  }

  throw new Error('Mock mode is enabled for Stage 10 but no mock implementation exists.');
}

// ============================================================================
// STAGE 11: SYNOPSIS GENERATION
// ============================================================================

/** The three legal document layouts supported by the Stage 11 backend. */
export type SolicitationInstrument = 'combined_synopsis' | 'sf18' | 'sf1449';

export interface SolicitationAttestation {
  /** The text shown to the contracting user for the chosen path. */
  statement: string;
  acknowledged: boolean;
}

export interface SolicitationDecision {
  instrument: SolicitationInstrument | null;
  rationale: string;
  attestation: SolicitationAttestation;
  status: 'draft' | 'confirmed' | 'issued';
  confirmed_by?: string;
  confirmed_at?: string;
  template_revision?: string;
}

export interface SolicitationCapability {
  /** Server capability name used by the typed Stage 11 endpoints. */
  can_edit_draft?: boolean;
  can_select_instrument?: boolean;
  can_confirm?: boolean;
  can_confirm_for_issuance?: boolean;
  can_issue?: boolean;
  can_generate_draft_preview?: boolean;
  official_form_issuance_enabled?: boolean;
  /** Server-selected, supported missing/stale prerequisites for formal issuance. */
  official_actions?: MarketResearchOfficialActions;
}

export interface SolicitationProvenance {
  source: string;
  path?: string;
  status: 'research_evidence' | 'draft' | 'confirmed';
}

export interface SolicitationReadinessItem {
  id: string;
  label: string;
  complete: boolean;
  detail?: string;
}

export interface SolicitationLineItem {
  line_number: string;
  quantity: string;
  uom: string;
  description: string;
  attachment_reference?: string;
}

export interface SolicitationVersion {
  id: string;
  event: 'draft_saved' | 'confirmed' | 'preview_generated' | 'issued' | 'amendment';
  actor?: string;
  created_at: string;
  snapshot_hash?: string;
}

export interface SolicitationPreview {
  id?: string;
  stale: boolean;
  generated_at?: string;
  watermark?: 'DRAFT — NOT FOR ISSUANCE';
}

export interface SolicitationArtifact {
  filename: string;
  content_disposition?: string;
  template_revision?: string;
  version_id?: string;
}

/**
 * Fields which exist only on one supported legal instrument. The browser
 * retains each valid group in the draft. The renderer maps only the selected
 * group into its chosen form, so inactive data is not treated as common data.
 */
export interface SolicitationInstrumentDetails {
  combined?: {
    public_notice_text?: string;
    site_visit_information?: string;
  };
  sf18?: {
    requisition_number?: string;
    delivery_address?: string;
    /** SF18 Block 5b; the common solicitation contact is still the preferred prefill. */
    information_contact_name?: string;
    /** SF18 Block 5b telephone number. */
    information_contact_phone?: string;
    /** SF18 Block 5b area-code cell, retained separately from the telephone number. */
    information_contact_area_code?: string;
    /** SF18 Block 6; use when delivery terms do not include a clear date. */
    deliver_by_date?: string;
    /** SF18 Block 7 “Other (See Schedule)” instructions, when FOB is not the only term. */
    delivery_other?: string;
    /** SF18 Block 8. An authorized addressee only; never prefill from market research. */
    recipient_name?: string;
    recipient_company?: string;
    recipient_address?: string;
    recipient_street_address?: string;
    recipient_city?: string;
    recipient_state?: string;
    recipient_zip?: string;
    /** SF18 Block 9 destination/consignee profile. */
    destination_consignee_name?: string;
    destination_street_address?: string;
    destination_city?: string;
    destination_state?: string;
    destination_zip_code?: string;
    /** SF18 provision/representation attachment indicator. */
    additional_provisions_attached?: 'yes' | 'no' | '';
  };
  sf1449?: {
    /** SF1449 Block 1. It is optional in a solicitation draft when unavailable. */
    requisition_number?: string;
    /** SF1449 Block 9 issuing-office code. */
    issuing_office_code?: string;
    /** SF1449 Block 10 partial-set-aside percentage, when applicable. */
    set_aside_percentage?: string;
    /** SF1449 Block 10's top-level acquisition checkbox selection. */
    acquisition_status?: 'unrestricted' | 'set_aside' | '';
    /** SF1449 Block 10 small-business program, when acquisition_status is set aside. */
    set_aside_program?: string;
    /** SF1449 Block 13a DPAS checkbox decision. */
    dpas_rated_order?: 'yes' | 'no' | '';
    /** SF1449 Block 14 (RFQ/IFB/RFP), not a contract award type. */
    method_of_solicitation?: 'RFQ' | 'IFB' | 'RFP' | '';
    /** Compatibility value retained from early Stage 11 drafts. */
    contract_type?: string;
    /** SF1449 Block 15 delivery profile. */
    deliver_to_code?: string;
    deliver_to_address?: string;
    /** SF1449 Block 16 administration profile; optional at solicitation. */
    award_administration_code?: string;
    award_administration_office?: string;
    /** SF1449 Block 27 checkbox decisions. */
    far_52_212_3_attached?: 'yes' | 'no' | '';
    addenda_attached?: 'yes' | 'no' | '';
    /** SF1449 Block 28, the number of signed copies the Government requests. */
    copies_to_return?: string;
  };
}

export type SolicitationFieldMappingDisplayGroup =
  | 'prefilled'
  | 'contracting_input'
  | 'offeror_return'
  | 'post_award_blank';

/** Display metadata returned by the server for the selected-form mapping UI. */
export interface SolicitationFieldMapping {
  /** Backend namespace (for example core, offeror, or instrument_details.sf18). */
  group: string;
  label?: string;
  target: string;
  /** Canonical source path returned by the typed Stage 11 backend. */
  source_path?: string;
  /** Compatibility alias for older mapping responses. */
  source?: string;
  value?: unknown;
  status?: string;
  provenance?: unknown;
  blank_reason?: string;
  issuance_blocker?: boolean;
}

/**
 * The common, renderer-independent record. `administrative` remains accepted
 * on older Stage 11 responses; the UI derives the same fallback mapping when
 * this newer envelope is absent.
 */
export interface SolicitationCanonicalCore {
  requirement_title?: string;
  solicitation_number?: string;
  delivery?: string;
  issuing_office?: string;
  contracting_officer?: string;
  field_mappings?: Partial<Record<'combined' | 'sf18' | 'sf1449', SolicitationFieldMapping[]>>;
}

export interface SolicitationCore {
  administrative: Record<string, unknown>;
  schedule: Array<Record<string, unknown>>;
  provenance: Record<string, unknown>;
}

export interface SolicitationFieldMappingResponse {
  instrument_type: 'combined' | 'sf18' | 'sf1449';
  fields: SolicitationFieldMapping[];
}

export interface SolicitationDraft {
  decision: SolicitationDecision;
  line_items: SolicitationLineItem[];
  readiness: SolicitationReadinessItem[];
  capabilities: SolicitationCapability;
  provenance?: Record<string, SolicitationProvenance>;
  preview?: SolicitationPreview;
  versions?: SolicitationVersion[];
}

/** The persisted state returned by the typed Stage 11 solicitation endpoints. */
export interface SolicitationStage11State {
  instrument_type: 'combined' | 'sf18' | 'sf1449' | null;
  selection_rationale: string;
  authority_attestation: string;
  line_items: Array<Record<string, unknown>>;
  administrative: Record<string, unknown>;
  /** Canonical typed core; legacy root administrative/line_items are mirrors. */
  core?: SolicitationCore;
  /** Mapping for the selected instrument only. */
  field_mapping?: SolicitationFieldMappingResponse;
  /** Transitional response compatibility, superseded by core/field_mapping. */
  canonical_core?: SolicitationCanonicalCore;
  instrument_details?: SolicitationInstrumentDetails;
  unresolved_policy_items: string[];
  recommendation?: {
    status?: 'decision_required' | 'nonbinding_recommendation';
    instrument_type?: 'combined' | 'sf18' | 'sf1449' | null;
    basis?: string[];
  };
  confirmation?: { status?: 'draft' | 'confirmed'; confirmed_by?: string; confirmed_at?: string; snapshot_hash?: string };
  preview?: { status?: 'not_generated' | 'stale' | 'current' | 'legacy_current'; source?: 'legacy_synopsis'; snapshot_hash?: string; generated_at?: string; artifact?: SolicitationArtifact };
  generated_document?: { status?: 'not_generated' | 'stale' | 'current'; artifact?: SolicitationArtifact };
  issuance?: { status?: 'not_issued' | 'issued'; issued_by?: string; issued_at?: string; artifact?: SolicitationArtifact };
  audit_events?: Array<{ event: string; actor?: string; at?: string }>;
  amendment_required?: boolean;
}

export interface SolicitationReadiness {
  ready: boolean;
  blockers: Array<{ code: string; message: string }>;
}

/** How the backend rendered the generated PDF. Present only after generation. */
export interface SolicitationRender {
  requested_form: 'combined' | 'sf18' | 'sf1449';
  rendered_form: 'combined' | 'sf18' | 'sf1449';
  renderer: 'form_style_fallback' | 'official_template' | 'product_generated';
  template_reference?: string;
}

export interface SolicitationStateResponse {
  state: SolicitationStage11State;
  readiness: SolicitationReadiness;
  field_mapping?: SolicitationFieldMappingResponse;
  /** Explicit values entered by an author for extraction questions. */
  author_answers?: Record<string, string>;
  /** Renderer identity returned by the generation endpoint. */
  render?: SolicitationRender;
}

export interface SolicitationCapabilitiesResponse {
  available: boolean;
  capabilities?: SolicitationCapability;
}

const SOLICITATION_MUTABLE_KEYS = [
  'instrument_type',
  'selection_rationale',
  'authority_attestation',
  'line_items',
  'administrative',
  'instrument_details',
  'unresolved_policy_items',
] as const;

function solicitationError(message: string, status: number, body?: any): Error & { status: number; error_code?: string; technical_detail?: string } {
  const error = new Error(message) as Error & { status: number; error_code?: string; technical_detail?: string };
  error.status = status;
  error.error_code = body?.error_code ?? body?.detail?.error_code;
  error.technical_detail = body?.technical_detail ?? body?.detail?.technical_detail;
  return error;
}

async function typedSolicitationRequest<T>(
  endpoint: string,
  options: { method?: 'GET' | 'POST'; body?: unknown; allowUnavailable?: boolean } = {},
): Promise<T | undefined> {
  const response = await fetchWithChatFeedback(`${API_BASE_URL}${endpoint}`, {
    method: options.method ?? 'GET',
    headers: options.method === 'POST' ? { 'Content-Type': 'application/json' } : undefined,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    credentials: 'include',
  });
  const body = await response.json().catch(() => undefined);
  if (options.allowUnavailable && response.status === 404) return undefined;
  if (!response.ok) {
    const detail = body?.detail;
    const message = body?.error ?? (typeof detail === 'string' ? detail : detail?.error) ?? USER_FACING_ERROR_MESSAGES.maintainers_alerted;
    throw solicitationError(message, response.status, body);
  }
  return body as T;
}

/** Detect the typed feature safely. Only an explicit 404 enables legacy fallback. */
export async function getSolicitationCapabilities(flowId: string): Promise<SolicitationCapabilitiesResponse> {
  const result = await typedSolicitationRequest<any>(
    `/api/synopsis/solicitation-capabilities?flow_id=${encodeURIComponent(flowId)}`,
    { allowUnavailable: true },
  );
  if (!result) return { available: false };
  const { success: _success, ...capabilities } = result;
  return { available: true, capabilities };
}

export async function loadSolicitationStage11(flowId: string): Promise<SolicitationStateResponse> {
  const result = await typedSolicitationRequest<any>(
    `/api/synopsis/solicitation-state?flow_id=${encodeURIComponent(flowId)}`,
  );
  return { state: result!.state, readiness: result!.readiness, field_mapping: result!.field_mapping, author_answers: result!.author_answers };
}

/** Strip all server-owned/audit fields before sending a draft mutation. */
export function mutableSolicitationState(state: SolicitationStage11State): Pick<SolicitationStage11State, typeof SOLICITATION_MUTABLE_KEYS[number]> {
  const mutable = SOLICITATION_MUTABLE_KEYS.reduce((result, key) => {
    result[key] = state[key];
    return result;
  }, {} as any);
  // Hydrated rows may include provenance, price, confirmation, and renderer
  // fields. The browser is permitted to submit only the requirement columns.
  mutable.line_items = (state.line_items || []).map((row) => ({
    clin_number: row.clin_number,
    quantity: row.quantity,
    uom: row.uom,
    description: row.description,
    attachment_reference: row.attachment_reference,
  }));
  // Preserve every valid instrument namespace so a contracting user can switch
  // back without re-entering data. The backend maps only the selected group
  // into the artifact and treats inactive groups as draft-only data.
  const detailFields: Record<keyof SolicitationInstrumentDetails, readonly string[]> = {
    combined: ['public_notice_text', 'site_visit_information'],
    sf18: [
      'requisition_number', 'delivery_address', 'information_contact_name', 'information_contact_phone',
      'deliver_by_date', 'recipient_name', 'recipient_company', 'recipient_address',
      'recipient_street_address', 'recipient_city', 'recipient_state', 'recipient_zip',
      'destination_consignee_name', 'destination_street_address', 'destination_city',
      'destination_state', 'destination_zip_code', 'additional_provisions_attached',
    ],
    sf1449: [
      'requisition_number', 'issuing_office_code', 'set_aside_percentage', 'method_of_solicitation', 'contract_type',
      'deliver_to_code', 'deliver_to_address', 'award_administration_code', 'award_administration_office',
      'far_52_212_3_attached', 'addenda_attached', 'copies_to_return',
    ],
  };
  mutable.instrument_details = Object.entries(detailFields).reduce((result, [instrument, fields]) => {
    const group = state.instrument_details?.[instrument as keyof SolicitationInstrumentDetails];
    if (!group || typeof group !== 'object') return result;
    const normalized = fields.reduce((values, field) => {
      const value = group[field as keyof typeof group];
      if (typeof value === 'string') values[field] = value;
      return values;
    }, {} as Record<string, string>);
    if (Object.keys(normalized).length > 0) result[instrument] = normalized;
    return result;
  }, {} as Record<string, Record<string, string>>);
  return mutable;
}

export async function saveSolicitationDraft(
  flowId: string,
  state: SolicitationStage11State,
  options: { includeInstrument?: boolean; authorAnswers?: Record<string, string> } = {},
): Promise<SolicitationStateResponse> {
  const mutable = mutableSolicitationState(state) as Record<string, unknown>;
  if (options.includeInstrument === false) delete mutable.instrument_type;
  const result = await typedSolicitationRequest<any>('/api/synopsis/solicitation-draft', {
    method: 'POST',
    body: { flow_id: flowId, state: mutable, ...(options.authorAnswers ? { author_answers: options.authorAnswers } : {}) },
  });
  return { state: result!.state, readiness: result!.readiness, field_mapping: result!.field_mapping, author_answers: result!.author_answers };
}

export async function preflightSolicitation(flowId: string): Promise<SolicitationStateResponse> {
  const result = await typedSolicitationRequest<any>('/api/synopsis/solicitation-preflight', { method: 'POST', body: { flow_id: flowId } });
  return { state: result!.state, readiness: result!.readiness, field_mapping: result!.field_mapping };
}

export async function previewSolicitation(flowId: string): Promise<SolicitationStateResponse> {
  const result = await typedSolicitationRequest<any>('/api/synopsis/solicitation-preview', { method: 'POST', body: { flow_id: flowId } });
  return { state: result!.state, readiness: { ready: false, blockers: [] }, field_mapping: result!.field_mapping };
}

export async function confirmSolicitation(flowId: string, expectedReviewToken?: string): Promise<SolicitationStateResponse> {
  const result = await typedSolicitationRequest<any>('/api/synopsis/solicitation-confirm', { method: 'POST', body: {
    flow_id: flowId, ...(expectedReviewToken ? { expected_review_token: expectedReviewToken } : {}),
  } });
  return { state: result!.state, readiness: result!.readiness, field_mapping: result!.field_mapping };
}

export async function issueSolicitation(flowId: string): Promise<SolicitationStateResponse> {
  const result = await typedSolicitationRequest<any>('/api/synopsis/solicitation-issue', { method: 'POST', body: { flow_id: flowId } });
  return { state: result!.state, readiness: { ready: true, blockers: [] }, field_mapping: result!.field_mapping };
}

/** Generate the selected Stage 11 form after server-side required-field and authorization checks. */
export async function generateSolicitationPDF(flowId: string): Promise<SolicitationStateResponse> {
  const result = await typedSolicitationRequest<any>('/api/synopsis/solicitation-generate', { method: 'POST', body: { flow_id: flowId } });
  return {
    state: result!.state,
    readiness: result!.readiness ?? { ready: true, blockers: [] },
    field_mapping: result!.field_mapping,
    render: result!.render,
  };
}

/** View/download persisted selected-form content without saving or generating lifecycle artifacts. */
export async function downloadSavedSolicitation(flowId: string): Promise<void> {
  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/synopsis/solicitation-saved/download?flow_id=${encodeURIComponent(flowId)}`, {
    method: 'GET', credentials: 'include',
  });
  if (!response.ok) throw new Error(response.status === 422 || response.status === 404
    ? USER_FACING_ERROR_MESSAGES.saved_output_unavailable : USER_FACING_ERROR_MESSAGES.maintainers_alerted);
  const blob = await response.blob();
  const filename = response.headers.get('Content-Disposition')?.match(/filename="?([^";]+)"?/i)?.[1] || 'saved-solicitation.pdf';
  const url = window.URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = url; anchor.download = filename;
    document.body.appendChild(anchor); anchor.click(); anchor.remove();
  } finally { window.URL.revokeObjectURL(url); }
}

/** Download the mutable generated form selected by the server. */
export async function downloadGeneratedSolicitation(flowId: string): Promise<void> {
  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/synopsis/solicitation-generated/download?flow_id=${encodeURIComponent(flowId)}`, {
    method: 'GET',
    credentials: 'include',
  });
  if (!response.ok) {
    const body = await response.json().catch(() => undefined);
    throw solicitationError(body?.error ?? body?.detail ?? 'Unable to download the generated solicitation.', response.status, body);
  }
  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition') || '';
  const filename = disposition.match(/filename="?([^";]+)"?/i)?.[1] || 'generated-solicitation.pdf';
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
}

/** Download only the immutable artifact selected by the server after issuance. */
export async function downloadIssuedSolicitation(flowId: string): Promise<void> {
  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/synopsis/solicitation-issued/download?flow_id=${encodeURIComponent(flowId)}`, {
    method: 'GET',
    credentials: 'include',
  });
  if (!response.ok) {
    const body = await response.json().catch(() => undefined);
    throw solicitationError(body?.error ?? body?.detail ?? 'Unable to download the issued solicitation.', response.status, body);
  }
  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition') || '';
  const filename = disposition.match(/filename="?([^";]+)"?/i)?.[1] || 'issued-solicitation.pdf';
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
}

/**
 * Keeps the new typed Stage 11 state compatible with the existing
 * `/api/synopsis/save-answers` persistence contract. The backend can hydrate
 * this same property into its first-class solicitation draft model.
 */
export function buildSynopsisSolicitationAnswers(
  answers: Record<string, unknown>,
  lineItems: unknown[],
  attachments: unknown[],
  decision: SolicitationDecision,
): Record<string, unknown> {
  return {
    ...answers,
    line_items: lineItems,
    attachments,
    solicitation_decision: decision,
  };
}

// 1. Get synopsis context questions
export async function getSynopsisQuestions(
  flowId?: string | null,
  forceRecompute: boolean = false
): Promise<ApiResponse<any>> {
  try {
    const response: any = await apiCall<any>(
      '/api/synopsis/questions',
      'POST',
      {
        ...(flowId ? { flow_id: flowId } : {}),
        force_recompute: forceRecompute,
      }
    );

    if (response.success && response.questions) {
      return {
        success: true,
        // Return both questions and metadata in the data object
        data: {
          questions: response.questions,
          metadata: response.metadata || {},
        },
        timestamp: new Date().toISOString(),
      };
    }

    const err = new Error(response.error ?? USER_FACING_ERROR_MESSAGES.maintainers_alerted);
    (err as any).error_code = response.error_code;
    (err as any).technical_detail = response.technical_detail;
    throw err;
  } catch (error) {
    console.error('Error generating synopsis questions:', error);
    throw error;
  }
}

// 2. Save synopsis answers
export async function saveSynopsisAnswers(
  flowId: string,
  answers: Record<string, any>
): Promise<ApiResponse<any>> {
  try {
    const response = await apiCall<any>(
      '/api/synopsis/save-answers',
      'POST',
      {
        ...(flowId ? { flow_id: flowId } : {}),
        answers: answers,
      }
    );

    if (response.success) {
      return {
        success: true,
        data: response.data,
        timestamp: new Date().toISOString(),
      };
    }

    const err = new Error(response.error || USER_FACING_ERROR_MESSAGES.maintainers_alerted);
    (err as any).error_code = response.error_code;
    (err as any).technical_detail = response.technical_detail;
    throw err;
  } catch (error) {
    console.error('Error saving synopsis answers:', error);
    throw error;
  }
}

// 3. Generate synopsis
export async function generateSynopsis(
  flowId?: string | null
): Promise<ApiResponse<any>> {
  if (!shouldUseMock(11)) {  // Use stage number 11
    try {
      const response: any = await apiCall<any>(
        "/api/synopsis/generate",
        "POST",
        {
          ...(flowId ? { flow_id: flowId } : {}),
        }
      );

      if (response.success && response.synopsis) {
        return {
          success: true,
          data: response.synopsis,
          timestamp: new Date().toISOString(),
        };
      }

      const err = new Error(response.error ?? USER_FACING_ERROR_MESSAGES.maintainers_alerted);
      (err as any).error_code = response.error_code;
      (err as any).technical_detail = response.technical_detail;
      throw err;
    } catch (error) {
      console.error("Error generating synopsis:", error);
      throw error;
    }
  }

  throw new Error('Mock mode is enabled for Stage 11 but no mock implementation exists.');
}

// ============================================================================
// USER FEEDBACK
// ============================================================================

export async function submitFeedback(
  message: string,
  workflow?: string,
  stage?: string,
  attachments: File[] = [],
  submissionId?: string,
  source?: import('../feedback/feedbackSourceContext').FeedbackSourceTuple,
  question?: string,
): Promise<ApiResponse<null>> {
  try {
    const body = new FormData();
    body.append('message', message);
    if (workflow !== undefined) body.append('workflow', workflow);
    if (stage !== undefined) body.append('stage', stage);
    if (question) body.append('question', question.slice(0, 2000));
    if (submissionId) body.append('submission_id', submissionId);
    if (source) {
      body.append('source_page', source.sourcePage);
      body.append('source_area', source.sourceArea);
      body.append('source_confidence', source.sourceConfidence);
    }
    attachments.forEach((attachment) => body.append('attachments', attachment));

    // Do not set Content-Type: the browser supplies the multipart boundary.
    const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/feedback`, {
      method: 'POST',
      body,
      credentials: 'include',
    });
    if (response.status === 401) window.dispatchEvent(new Event('auth-expired'));
    let payload: ApiResponse<null>;
    try {
      payload = await response.json();
    } catch {
      payload = {
        success: false,
        error: USER_FACING_ERROR_MESSAGES.unrecognized,
        timestamp: new Date().toISOString(),
      };
    }
    if (!response.ok) {
      const normalized = normalizeApiError(payload);
      return {
        success: false,
        data: null,
        error: normalized.message,
        error_code: normalized.errorCode,
        technical_detail: normalized.technicalDetail,
        timestamp: new Date().toISOString(),
      };
    }
    return {
      success: payload.success,
      data: null,
      message: payload.message,
      error: payload.error,
      error_code: payload.error_code,
      technical_detail: payload.technical_detail,
      timestamp: new Date().toISOString(),
    };
  } catch (error) {
    return {
      success: false,
      data: null,
      error: error instanceof Error ? error.message : 'Failed to send feedback',
      timestamp: new Date().toISOString(),
    };
  }
}

export const createFeedbackSubmissionId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const value = Math.floor(Math.random() * 16);
    return (char === 'x' ? value : (value & 0x3) | 0x8).toString(16);
  });
};

/**
 * Submit feedback about a specific Ask FAR chat answer (the per-answer "Report
 * an issue" control). Reuses the shared `/api/feedback` endpoint — same Gmail
 * send, recipients, and per-user rate limit — but attaches the flagged Q&A so
 * maintainers can see exactly which answer is being reported.
 *
 * `surface` is a friendly label ("Ask FAR" | "Homepage chat") sent as the
 * `workflow` field; it also keys the backend's per-page rate-limit bucket, so
 * chat feedback is throttled independently of the navbar feedback dialog.
 */
export async function submitChatFeedback(params: {
  message: string;
  surface: string;
  question?: string;
  answer?: string;
  turnId?: string;
  contextId?: string | null;
  submissionId?: string;
  source?: import('../feedback/feedbackSourceContext').FeedbackSourceTuple;
}): Promise<ApiResponse<null>> {
  const {
    message,
    surface,
    question,
    answer,
    turnId,
    contextId,
    submissionId = createFeedbackSubmissionId(),
    source,
  } = params;
  try {
    const response = await apiCall<null>('/api/feedback', 'POST', {
      message,
      workflow: surface,
      question,
      answer,
      turn_id: turnId,
      context_id: contextId ?? undefined,
      submission_id: submissionId,
      ...(source ? {
        source_page: source.sourcePage,
        source_area: source.sourceArea,
        source_confidence: source.sourceConfidence,
      } : {}),
    });
    return {
      success: response.success,
      data: null,
      error: response.error,
      error_code: response.error_code,
      technical_detail: response.technical_detail,
      timestamp: new Date().toISOString(),
    };
  } catch (error) {
    return {
      success: false,
      data: null,
      error: error instanceof Error ? error.message : 'Failed to send feedback',
      timestamp: new Date().toISOString(),
    };
  }
}

/** Authoritative pending prompt, shared across sessions and chat surfaces. */
export interface ChatFeedbackPending {
  prompt_id: string;
  surface: 'homechat' | 'farchat';
  turn_id: string;
  context_id?: string | null;
  question?: string | null;
  answer?: string | null;
  /** Exact rated turn recovered from its authorized transcript after reload. */
  answer_turn?: RawV2Turn;
  mandatory?: boolean;
}

/** A failed recovery must not unlock the application as if no prompt exists. */
export async function fetchPendingChatFeedback(): Promise<ChatFeedbackPending | null> {
  const response = await apiCall<{ pending: ChatFeedbackPending | null }>(
    '/api/chat-feedback/pending', 'GET', undefined, { retries: 0, timeoutMs: 15000 }
  );
  if (!response.success || !response.data || !('pending' in response.data)) {
    throw new Error(response.error || 'Could not check required feedback. Please try again.');
  }
  return response.data.pending;
}

/** Submit required feedback. The caller must await success before unlocking. */
export async function submitChatFeedbackPrompt(params: {
  promptId: string;
  rating: 'bad' | 'fine' | 'good';
  turnId?: string;
  contextId?: string | null;
  surface?: string;
  goodComment?: string;
  badComment?: string;
}): Promise<ApiResponse<null>> {
  const {
    promptId,
    rating,
    turnId,
    contextId,
    surface,
    goodComment,
    badComment,
  } = params;
  try {
    const response = await apiCall<null>('/api/chat-feedback', 'POST', {
      prompt_id: promptId,
      rating,
      turn_id: turnId,
      context_id: contextId ?? undefined,
      surface,
      good_comment: goodComment,
      bad_comment: badComment,
    });
    if (!response.success) {
      console.warn('Chat feedback prompt submit failed:', response.error);
    }
    return {
      success: response.success,
      data: null,
      error: response.error,
      error_code: response.error_code,
      technical_detail: response.technical_detail,
      timestamp: new Date().toISOString(),
    };
  } catch (error) {
    console.warn('Chat feedback prompt submit failed:', error);
    return {
      success: false,
      data: null,
      error: error instanceof Error ? error.message : 'Failed to send feedback',
      timestamp: new Date().toISOString(),
    };
  }
}

/**
 * Download the server-assembled streamlined Market Research Report.
 *
 * The server reads the saved workflow records after authorizing `flowId`; the
 * browser deliberately sends no report content, draft values, or form data.
 */
export async function downloadStreamlinedMrr(flowId: string): Promise<void> {
  const safeFlowId = flowId.trim();
  if (!safeFlowId) throw new Error(USER_FACING_ERROR_MESSAGES.workflow_unavailable);

  let response: Response;
  try {
    response = await fetchWithChatFeedback(
      `${API_BASE_URL}/api/market-research/${encodeURIComponent(safeFlowId)}/streamlined-mrr.pdf`,
      { method: 'POST', credentials: 'include' },
    );
  } catch {
    throw new Error(USER_FACING_ERROR_MESSAGES.unrecognized);
  }

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    const normalized = normalizeApiError(payload);
    const error = new Error(USER_FACING_ERROR_MESSAGES.unrecognized) as Error & {
      error_code?: string;
      technical_detail?: string;
    };
    error.error_code = normalized.errorCode;
    error.technical_detail = normalized.technicalDetail;
    throw error;
  }

  const contentType = response.headers.get('Content-Type')?.split(';')[0].trim().toLowerCase();
  const blob = await response.blob();
  const bytes = new Uint8Array(await blob.arrayBuffer());
  if (contentType !== 'application/pdf' || bytes.length < 5
      || String.fromCharCode(...Array.from(bytes.slice(0, 5))) !== '%PDF-') {
    throw new Error(USER_FACING_ERROR_MESSAGES.unrecognized);
  }

  const disposition = response.headers.get('Content-Disposition') ?? '';
  const candidate = disposition.match(/filename="?([^";]+)"?/i)?.[1]
    ?? 'streamlined-market-research-report.pdf';
  const filename = candidate.replace(/[^A-Za-z0-9._-]/g, '_') || 'streamlined-market-research-report.pdf';
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
}

/** A server-owned source shown alongside an MRR review value. */
export interface MarketResearchMrrSource {
  key: string;
  label: string;
}

export interface MarketResearchMrrCandidate extends MarketResearchMrrSource {
  value: string | string[];
}

export type MarketResearchMrrReviewValue = string | string[];

/**
 * The client deliberately renders this server-authored field envelope rather
 * than deriving a value, provenance, conflict, or completion state itself.
 */
export interface MarketResearchMrrReviewField {
  value: MarketResearchMrrReviewValue;
  status: string;
  source: MarketResearchMrrSource | null;
  candidates: MarketResearchMrrCandidate[];
  conflict: boolean;
  /** Server classification: show the settled value as inherited context. */
  readonly: boolean;
}

/** Server-derived applicability and human decisions; research prose cannot complete these records. */
export interface MarketResearchOfficialApplicability {
  agency: string; component: string; acquisition_type: string; procedure: string;
  threshold_basis: string; action: string; commerciality: string; part12: string; commercial_definition: string;
  prior_cid_reference: string; required_use_contract: string; commerciality_evidence_reference: string; commerciality_date: string;
  anticipated_value: number | null; relevant_date: string; regime: string; authority_reference: string;
}
export interface MarketResearchOfficialActionCommand {
  official_revision: number; official_review_token: string; action_key: string; decision: 'completed' | 'exception';
  role: string; date: string; authority_reference: string; evidence_reference: string;
  outcome?: 'commercial' | 'noncommercial'; exception_basis?: string; approver?: string; expires_on?: string; scope_reference?: string; delegation_reference?: string;
}
export interface MarketResearchOfficialActions {
  schema_version: 1; revision: number; review_token: string; can_record_actions: boolean; status: 'ready' | 'pending' | 'unresolved';
  applicability_facts: MarketResearchOfficialApplicability & { provenance: string };
  profile: { key: string; version: string; status: 'verified' | 'unresolved'; reference: string; effective_from: string; effective_to: string };
  actions: Array<{
    key: string; label: string; applicability: 'required' | 'not_applicable' | 'unresolved';
    rationale: string; reference: string; owner_role: string; boundary: string;
    completion: 'recorded' | 'not_recorded' | 'stale' | 'unknown' | 'not_applicable';
    allow_exception?: boolean; exception_bases?: string[];
    currency: 'current' | 'stale' | 'unknown'; evidence_references: string[];
    record?: Omit<MarketResearchOfficialActionCommand, 'official_revision' | 'official_review_token'> & { recorded_by: string; recorded_at: string };
  }>;
}

export interface MarketResearchHandoff {
  schema_version: 1;
  procedure: string;
  form: { code: 'application_native' | 'local_profile'; title: string; version: string; reference: string; status: 'unresolved' | 'recorded'; required_signers: string[]; notice: string };
  draft_status: 'best_effort' | 'saved';
  research_status: 'unknown' | 'in_progress' | 'complete';
  review_status: 'not_ready' | 'ready';
  approval_status: 'unresolved' | 'not_required' | 'not_recorded' | 'recorded' | 'stale';
  notices: string[];
  available_records: Array<{ key: string; label: string; status: string; owner: string; detail: string }>;
  outstanding_actions: Array<{ key: string; label: string; status: string; owner: string; detail: string }>;
  signatures: Array<{ role: string; name: string; title: string; date: string; signature: string; evidence_reference: string; recorded_by: string; recorded_at: string; status: string }>;
  captured: boolean;
  context_stale: boolean;
  revision: number;
  can_record_actions: boolean;
  can_edit_profile: boolean;
  can_capture_handoff: boolean;
  contracting_records: Record<string, string>;
  official_actions?: MarketResearchOfficialActions;
}

export interface MarketResearchHandoffCommand {
  official_applicability?: MarketResearchOfficialApplicability;
  official_revision?: number;
  official_review_token?: string;
  profile?: Pick<MarketResearchHandoff['form'], 'title' | 'version' | 'reference' | 'required_signers'>;
  capture?: boolean;
  contracting_records?: Record<string, string>;
  revision?: number;
}

export interface MarketResearchHandoffAction {
  revision: number;
  role: string;
  name: string;
  title: string;
  date: string;
  signature: string;
  evidence_reference: string;
}

export function asMarketResearchHandoff(value: unknown): MarketResearchHandoff | undefined {
  const object = (v: unknown): v is Record<string, unknown> => Boolean(v) && typeof v === 'object' && !Array.isArray(v);
  const strings = (v: unknown) => Array.isArray(v) && v.every(x => typeof x === 'string');
  const fields = (v: unknown, keys: string[]) => object(v) && keys.every(key => typeof v[key] === 'string');
  if (!object(value)) return undefined;
  const item = value;
  const form = item.form;
  const roles = ['requiring_activity', 'contracting_officer', 'small_business', 'legal', 'competition_advocate', 'additional', 'approval_authority'];
  if (item.schema_version !== 1 || !fields(form, ['title', 'version', 'reference', 'notice']) || !object(form)
    || !['application_native', 'local_profile'].includes(String(form.code)) || !['unresolved', 'recorded'].includes(String(form.status))
    || !strings(form.required_signers) || !(form.required_signers as string[]).every(role => roles.includes(role))
    || typeof item.procedure !== 'string' || !Number.isInteger(item.revision) || Number(item.revision) < 0
    || !['best_effort', 'saved'].includes(String(item.draft_status))
    || !['unknown', 'in_progress', 'complete'].includes(String(item.research_status))
    || !['not_ready', 'ready'].includes(String(item.review_status))
    || !['unresolved', 'not_required', 'not_recorded', 'recorded', 'stale'].includes(String(item.approval_status))
    || !strings(item.notices)
    || !['captured', 'context_stale', 'can_record_actions', 'can_edit_profile', 'can_capture_handoff'].every(key => typeof item[key] === 'boolean')
    || !object(item.contracting_records) || !Object.values(item.contracting_records).every(v => typeof v === 'string')
    || !['available_records', 'outstanding_actions'].every(key => Array.isArray(item[key]) && (item[key] as unknown[]).every(v => fields(v, ['key', 'label', 'status', 'owner', 'detail'])))
    || !Array.isArray(item.signatures) || !item.signatures.every(v => fields(v, ['role', 'name', 'title', 'date', 'signature', 'evidence_reference', 'recorded_by', 'recorded_at', 'status']))) return undefined;
  if (item.official_actions !== undefined) {
    const official = item.official_actions;
    if (!object(official) || typeof official.review_token !== 'string' || !/^[a-f0-9]{64}$/.test(official.review_token) || official.schema_version !== 1 || !Number.isInteger(official.revision) || Number(official.revision) < 0
      || typeof official.can_record_actions !== 'boolean' || !['ready', 'pending', 'unresolved'].includes(String(official.status))
      || !fields(official.applicability_facts, ['agency', 'component', 'acquisition_type', 'procedure', 'relevant_date', 'regime', 'authority_reference', 'provenance', 'threshold_basis', 'action', 'commerciality', 'part12', 'commercial_definition', 'prior_cid_reference', 'required_use_contract', 'commerciality_evidence_reference', 'commerciality_date'])
      || !object(official.applicability_facts) || !(official.applicability_facts.anticipated_value === null || (typeof official.applicability_facts.anticipated_value === 'number' && Number.isFinite(official.applicability_facts.anticipated_value) && official.applicability_facts.anticipated_value >= 0))
      || !fields(official.profile, ['key', 'version', 'reference', 'effective_from', 'effective_to']) || !object(official.profile)
      || !['verified', 'unresolved'].includes(String(official.profile.status))
      || !Array.isArray(official.actions) || !official.actions.every(row => {
        if (!fields(row, ['key', 'label', 'rationale', 'reference', 'owner_role', 'boundary']) || !object(row)
          || !['required', 'not_applicable', 'unresolved'].includes(String(row.applicability))
          || !['recorded', 'not_recorded', 'stale', 'unknown', 'not_applicable'].includes(String(row.completion))
          || (row.allow_exception !== undefined && typeof row.allow_exception !== 'boolean')
          || (row.exception_bases !== undefined && !strings(row.exception_bases))
          || !['current', 'stale', 'unknown'].includes(String(row.currency)) || !strings(row.evidence_references)) return false;
        if (row.record === undefined) return true;
        const record = row.record;
        return fields(record, ['action_key', 'role', 'date', 'authority_reference', 'evidence_reference', 'recorded_by', 'recorded_at']) && object(record)
          && ['completed', 'exception'].includes(String(record.decision))
          && (record.outcome === undefined || ['commercial', 'noncommercial'].includes(String(record.outcome)))
          && ['exception_basis', 'approver', 'expires_on', 'scope_reference', 'delegation_reference'].every(key => record[key] === undefined || typeof record[key] === 'string');
      })) return undefined;
  }
  return value as unknown as MarketResearchHandoff;
}

export interface MarketResearchMrrDetailsResponse {
  handoff?: MarketResearchHandoff;
  details: Record<string, MarketResearchMrrReviewField>;
  /** Settled source-owned values shown for reference, never editable here. */
  inherited_context: Record<string, MarketResearchMrrReviewField>;
  classification_product?: MarketResearchMrrClassificationProduct;
}

export interface MarketResearchMrrClassificationProductOption {
  key: string;
  name: string;
  manufacturer: string;
  model: string;
  price: number | null;
  price_display: string;
}

export interface MarketResearchMrrClassificationProduct {
  selected_key: string;
  selection_source: 'default' | 'user' | 'none';
  options: MarketResearchMrrClassificationProductOption[];
  psc: string;
  naics: string;
  no_usable_prices: boolean;
  psc_editable?: boolean;
  naics_editable?: boolean;
  available_pscs?: string[];
  available_naics?: string[];
  psc_source?: string;
  naics_source?: string;
}

export type MarketResearchMrrReviewCommand = {
  changes?: Record<string, { mode: 'override'; value: string | string[] } | { mode: 'accept_candidate'; candidate_key: string }>;
  reset?: string[];
};

function asReviewValue(value: unknown): MarketResearchMrrReviewValue {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.filter((item): item is string => typeof item === 'string');
  return '';
}

function asMrrSource(value: unknown): MarketResearchMrrSource | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const source = value as Record<string, unknown>;
  return typeof source.key === 'string' && typeof source.label === 'string'
    ? { key: source.key, label: source.label }
    : null;
}

function asMrrCandidates(value: unknown): MarketResearchMrrCandidate[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((candidate) => {
    const source = asMrrSource(candidate);
    if (!source || !candidate || typeof candidate !== 'object') return [];
    const fieldValue = asReviewValue((candidate as Record<string, unknown>).value);
    return [{ ...source, value: fieldValue }];
  });
}

function asMrrReviewField(value: unknown): MarketResearchMrrReviewField {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const field = value as Record<string, unknown>;
    if (Object.prototype.hasOwnProperty.call(field, 'value')) {
      return {
        value: asReviewValue(field.value),
        status: typeof field.status === 'string' ? field.status : 'needs_input',
        source: asMrrSource(field.source),
        candidates: asMrrCandidates(field.candidates),
        conflict: field.conflict === true,
        readonly: field.readonly === true,
      };
    }
  }
  // The first MRR release stored flat string values. Keep those records
  // readable during rollout without fabricating a current-stage source.
  return {
    value: asReviewValue(value), status: 'legacy_override', source: null, candidates: [], conflict: false, readonly: true,
  };
}

function splitMrrMethods(value: MarketResearchMrrReviewValue): string[] {
  if (Array.isArray(value)) return value;
  return value.split(',').map((item) => item.trim()).filter(Boolean);
}

function normalizeMrrMethodField(field: MarketResearchMrrReviewField): MarketResearchMrrReviewField {
  return {
    ...field,
    value: splitMrrMethods(field.value),
    candidates: field.candidates.map((candidate) => ({
      ...candidate,
      value: splitMrrMethods(candidate.value),
    })),
  };
}

function asMrrReviewDetails(value: unknown): Record<string, MarketResearchMrrReviewField> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.entries(value as Record<string, unknown>).reduce<Record<string, MarketResearchMrrReviewField>>(
    (details, [key, field]) => {
      const reviewField = asMrrReviewField(field);
      return {
        ...details,
        [key]: key === 'methods_used' ? normalizeMrrMethodField(reviewField) : reviewField,
      };
    },
    {},
  );
}

function asMrrClassificationProduct(value: unknown): MarketResearchMrrClassificationProduct | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const source = value as Record<string, unknown>;
  const options = Array.isArray(source.options) ? source.options.flatMap((item) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return [];
    const option = item as Record<string, unknown>;
    if (typeof option.key !== 'string' || typeof option.name !== 'string') return [];
    return [{
      key: option.key,
      name: option.name,
      manufacturer: typeof option.manufacturer === 'string' ? option.manufacturer : '',
      model: typeof option.model === 'string' ? option.model : '',
      price: typeof option.price === 'number' && Number.isFinite(option.price) && option.price > 0 ? option.price : null,
      price_display: typeof option.price_display === 'string' ? option.price_display : 'No usable price',
    }];
  }) : [];
  return {
    selected_key: typeof source.selected_key === 'string' ? source.selected_key : '',
    selection_source: source.selection_source === 'user' || source.selection_source === 'default' ? source.selection_source : 'none',
    options,
    psc: typeof source.psc === 'string' ? source.psc : '',
    naics: typeof source.naics === 'string' ? source.naics : '',
    no_usable_prices: source.no_usable_prices === true,
    ...Object.fromEntries(['psc_editable', 'naics_editable'].filter(key => typeof source[key] === 'boolean').map(key => [key, source[key]])),
    ...Object.fromEntries(['available_pscs', 'available_naics'].filter(key => Array.isArray(source[key])).map(key => [key, (source[key] as unknown[]).filter(code => typeof code === 'string')])),
    ...Object.fromEntries(['psc_source', 'naics_source'].filter(key => typeof source[key] === 'string').map(key => [key, source[key]])),
  };
}

function asMrrReviewResponse(value: unknown): MarketResearchMrrDetailsResponse {
  const envelope = value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
  return {
    ...(asMarketResearchHandoff(envelope.handoff) ? { handoff: asMarketResearchHandoff(envelope.handoff) } : {}),
    details: asMrrReviewDetails(Object.prototype.hasOwnProperty.call(envelope, 'details') ? envelope.details : envelope),
    inherited_context: asMrrReviewDetails(envelope.inherited_context),
    ...(Object.prototype.hasOwnProperty.call(envelope, 'classification_product')
      ? { classification_product: asMrrClassificationProduct(envelope.classification_product) }
      : {}),
  };
}

function marketResearchMrrDetailsError(payload: unknown): Error & {
  error_code?: string;
  technical_detail?: string;
} {
  const normalized = normalizeApiError(payload);
  const error = new Error(normalized.message) as Error & {
    error_code?: string;
    technical_detail?: string;
  };
  error.error_code = normalized.errorCode;
  error.technical_detail = normalized.technicalDetail;
  return error;
}

/** Load the narrow, persisted Stage-11 details model used by the MRR exporter. */
export async function loadMarketResearchMrrDetails(flowId: string): Promise<MarketResearchMrrDetailsResponse> {
  const safeFlowId = flowId.trim();
  if (!safeFlowId) throw new Error(USER_FACING_ERROR_MESSAGES.workflow_unavailable);
  let response: Response;
  try {
    response = await fetchWithChatFeedback(
      `${API_BASE_URL}/api/market-research/${encodeURIComponent(safeFlowId)}/streamlined-mrr`,
      { credentials: 'include' },
    );
  } catch {
    throw new Error(USER_FACING_ERROR_MESSAGES.unrecognized);
  }
  const body = await response.json().catch(() => undefined);
  if (!response.ok) throw marketResearchMrrDetailsError(body);

  const data = body && typeof body === 'object' && 'data' in body
    ? (body as { data?: unknown }).data
    : body;
  const envelope = data && typeof data === 'object' && !Array.isArray(data)
    ? data as Record<string, unknown>
    : {};
  return asMrrReviewResponse(envelope);
}

/** Persist only reviewed MRR-detail values; PDF content is never client supplied. */
export async function saveMarketResearchMrrDetails(
  flowId: string,
  command: MarketResearchMrrReviewCommand,
): Promise<MarketResearchMrrDetailsResponse> {
  const safeFlowId = flowId.trim();
  if (!safeFlowId) throw new Error(USER_FACING_ERROR_MESSAGES.workflow_unavailable);
  let response: Response;
  try {
    response = await fetchWithChatFeedback(
      `${API_BASE_URL}/api/market-research/${encodeURIComponent(safeFlowId)}/streamlined-mrr`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(command),
      },
    );
  } catch {
    throw new Error(USER_FACING_ERROR_MESSAGES.unrecognized);
  }
  const body = await response.json().catch(() => undefined);
  if (!response.ok) throw marketResearchMrrDetailsError(body);

  const data = body && typeof body === 'object' && 'data' in body
    ? (body as { data?: unknown }).data
    : body;
  const envelope = data && typeof data === 'object' && !Array.isArray(data)
    ? data as Record<string, unknown>
    : {};
  return asMrrReviewResponse(envelope);
}

async function requestMarketResearchHandoff(flowId: string, suffix: string, method: 'PUT' | 'POST', command: unknown): Promise<MarketResearchHandoff> {
  const safeFlowId = flowId.trim();
  if (!safeFlowId) throw new Error(USER_FACING_ERROR_MESSAGES.workflow_unavailable);
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/api/market-research/${encodeURIComponent(safeFlowId)}/handoff${suffix}`, {
      method, credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(command),
    });
  } catch { throw new Error(USER_FACING_ERROR_MESSAGES.unrecognized); }
  const body = await response.json().catch(() => undefined);
  if (!response.ok || body?.success === false) throw marketResearchMrrDetailsError(body);
  const data = body?.data ?? body;
  const handoff = asMarketResearchHandoff(data?.handoff ?? data);
  if (!handoff) throw new Error(USER_FACING_ERROR_MESSAGES.unrecognized);
  return handoff;
}

export const saveMarketResearchHandoff = (flowId: string, command: MarketResearchHandoffCommand): Promise<MarketResearchHandoff> =>
  requestMarketResearchHandoff(flowId, '', 'PUT', command);

export const recordMarketResearchHandoffAction = (flowId: string, action: MarketResearchHandoffAction | MarketResearchOfficialActionCommand): Promise<MarketResearchHandoff> =>
  requestMarketResearchHandoff(flowId, '/actions', 'POST', action);

// 4. Generate synopsis PDF
export async function generateSynopsisPDF(
  flowId: string
): Promise<void> {
  try {
    const API_BASE_URL = process.env.REACT_APP_API_URL || '';

    // Fetch PDF as blob
    const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/synopsis/generate-pdf`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ...(flowId ? { flow_id: flowId } : {}) }),
      credentials: 'include',
    });

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      const detail = body?.detail;
      // Structured detail (option 2 pattern from error_surfacing spec).
      if (detail && typeof detail === 'object' && 'error' in detail) {
        const err = new Error(detail.error || 'Failed to generate synopsis PDF') as Error & {
          error_code?: string;
          technical_detail?: string;
        };
        err.error_code = detail.error_code;
        err.technical_detail = detail.technical_detail;
        throw err;
      }
      throw new Error(typeof detail === 'string' ? detail : 'Failed to generate synopsis PDF');
    }

    // Get PDF blob
    const blob = await response.blob();

    // Get filename from header
    const contentDisposition = response.headers.get('Content-Disposition');
    const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
    const filename = filenameMatch ? filenameMatch[1] : 'Combined_Synopsis.pdf';

    // Create download link
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();

    // Cleanup
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);

    console.log(`✅ PDF downloaded: ${filename}`);
  } catch (error) {
    console.error('Error generating synopsis PDF:', error);
    throw error;
  }
}


// Utility function to handle API errors
export function handleApiError(error: any): ApiResponse<any> {
  return {
    success: false,
    error: error.message || 'An error occurred',
    timestamp: new Date().toISOString(),
  };
}

// ===========================
// Stage 12: Vendor Bids
// ===========================

// 1. Upload vendor quote PDFs
export async function uploadVendorQuotes(
  flowId: string,
  files: File[]
): Promise<ApiResponse<any>> {
  try {
    const API_BASE_URL = process.env.REACT_APP_API_URL || '';
    const userEmail = localStorage.getItem('userEmail') || '';

    const formData = new FormData();
    formData.append('flow_id', flowId);
    files.forEach((file) => {
      formData.append('files', file);
    });

    const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/vendor-bids/upload`, {
      method: 'POST',
      headers: {},
      body: formData,
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
      throw new Error(extractDetailMessage(error.detail, 'Failed to upload vendor quotes'));
    }

    const data = await response.json();
    return {
      success: true,
      data: data,
      timestamp: new Date().toISOString(),
    };
  } catch (error) {
    console.error('Error uploading vendor quotes:', error);
    throw error;
  }
}

// 2. Extract vendor bids from uploaded PDFs
export async function extractVendorBids(
  flowId: string
): Promise<ApiResponse<any>> {
  try {
    const response: any = await apiCall<any>(
      "/api/vendor-bids/extract",
      "POST",
      {
        ...(flowId ? { flow_id: flowId } : {}),
      }
    );

    if (response.success && response.bids) {
      return {
        success: true,
        data: response,
        timestamp: new Date().toISOString(),
      };
    }

    const err = new Error(response.error ?? USER_FACING_ERROR_MESSAGES.wait_and_retry);
    (err as any).error_code = response.error_code;
    (err as any).technical_detail = response.technical_detail;
    throw err;
  } catch (error) {
    console.error("Error extracting vendor bids:", error);
    throw error;
  }
}

// 3. Save vendor bids (after user corrections)
export async function saveVendorBids(
  flowId: string,
  bids: any[]
): Promise<ApiResponse<any>> {
  try {
    const response: any = await apiCall<any>(
      "/api/vendor-bids/save",
      "POST",
      {
        ...(flowId ? { flow_id: flowId } : {}),
        bids: bids,
      }
    );

    if (response.success) {
      return {
        success: true,
        data: response,
        timestamp: new Date().toISOString(),
      };
    }

    const err = new Error(response.error ?? USER_FACING_ERROR_MESSAGES.wait_and_retry);
    (err as any).error_code = response.error_code;
    (err as any).technical_detail = response.technical_detail;
    throw err;
  } catch (error) {
    console.error('Error saving vendor bids:', error);
    throw error;
  }
}

// 4. Generate Abstract of Offers PDF
export async function generateAbstractPDF(
  flowId: string
): Promise<void> {
  try {
    const API_BASE_URL = process.env.REACT_APP_API_URL || '';
    const userEmail = localStorage.getItem('userEmail') || '';

    // Fetch PDF as blob
    const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/vendor-bids/generate-abstract`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ...(flowId ? { flow_id: flowId } : {}) }),
      credentials: 'include',
    });

    if (!response.ok) {
      // Try to extract detailed error message from response
      let errorMessage: string = USER_FACING_ERROR_MESSAGES.maintainers_alerted;
      let errorCode: string | undefined;
      let technicalDetail: string | undefined;
      try {
        const errorData = await response.json();
        const detail = errorData.detail;
        // Structured detail (option 2 pattern from error_surfacing spec).
        if (detail && typeof detail === 'object' && 'error' in detail) {
          errorMessage = detail.error || errorMessage;
          errorCode = detail.error_code;
          technicalDetail = detail.technical_detail;
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        }
      } catch (parseError) {
        // If JSON parsing fails, keep the generic friendly message —
        // NEVER expose response.statusText (e.g. "Internal Server Error").
      }
      const err = new Error(errorMessage) as Error & {
        error_code?: string;
        technical_detail?: string;
      };
      if (errorCode) err.error_code = errorCode;
      if (technicalDetail) err.technical_detail = technicalDetail;
      throw err;
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = `abstract-of-offers-${Date.now()}.pdf`;
    document.body.appendChild(a);
    a.click();

    // Cleanup
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);

    console.log('Abstract of Offers PDF downloaded successfully');
  } catch (error) {
    console.error('Error generating abstract PDF:', error);
    throw error;
  }
}

// 5. Get Vendor Bids Form Data
export async function getVendorBidsFormData(
  flowId: string
): Promise<ApiResponse<any>> {
  try {
    const response: any = await apiCall<any>(
      `/api/vendor-bids/form-data?flow_id=${flowId}`,
      "GET"
    );

    if (response.success || response.data) {
      return {
        success: true,
        data: response.data || response,
        timestamp: new Date().toISOString(),
      };
    }

    const err = new Error(response.error ?? USER_FACING_ERROR_MESSAGES.wait_and_retry);
    (err as any).error_code = response.error_code;
    (err as any).technical_detail = response.technical_detail;
    throw err;
  } catch (error) {
    console.error('Error loading vendor bids form data:', error);
    throw error;
  }
}

// 6. Save Vendor Bids Form
export async function saveVendorBidsForm(
  flowId: string,
  formData: any
): Promise<ApiResponse<any>> {
  try {
    console.log('[saveVendorBidsForm] Saving form with checkboxes:', formData.checkboxes);
    console.log('[saveVendorBidsForm] Vendors being saved:', formData.vendors.map((v: any) => ({
      name: v.vendor_name,
      applicable: v.applicable,
      favorite: v.favorite,
    })));
    const response: any = await apiCall<any>(
      "/api/vendor-bids/save-form",
      "POST",
      {
        ...(flowId ? { flow_id: flowId } : {}),
        header: formData.header,
        vendors: formData.vendors,
        checkboxes: formData.checkboxes,
      }
    );

    if (response.success) {
      return {
        success: true,
        data: response.data,
        timestamp: new Date().toISOString(),
      };
    }

    const err = new Error(response.error ?? USER_FACING_ERROR_MESSAGES.wait_and_retry);
    (err as any).error_code = response.error_code;
    (err as any).technical_detail = response.technical_detail;
    throw err;
  } catch (error: any) {
    console.error('Error saving vendor bids form:', error);
    // Try to extract detailed error message from error object
    if (error.message) {
      throw new Error(error.message);
    }
    throw error;
  }
}


// ===========================
// Construction Abstract functions (Stage 502)
// ===========================

export async function uploadAndExtractProposals(
  flowId: string,
  proposals: Array<{ offerorName: string; file: File }>,
  noBids: Array<{ offerorName: string; reason?: string }>,
  forceRecompute: boolean = false,
  idempotencyKey: string = createVendorDiscoveryIdempotencyKey(),
): Promise<any> {
  const API_BASE_URL = process.env.REACT_APP_API_URL || '';

  const formData = new FormData();
  formData.append('flow_id', flowId);
  proposals.forEach((p) => {
    formData.append('offeror_names', p.offerorName);
    formData.append('proposal_files', p.file);
  });
  if (noBids.length > 0) {
    formData.append('no_bids', JSON.stringify(noBids));
  }
  formData.append('force_recompute', String(forceRecompute));

  const headers: Record<string, string> = {
    'Idempotency-Key': idempotencyKey,
  };

  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/construction/abstract/extract`, {
    method: 'POST',
    headers,
    body: formData,
    credentials: 'include',
  });

  if (!response.ok) {
    let errorMessage: string = USER_FACING_ERROR_MESSAGES.maintainers_alerted;
    try {
      const errorData = await response.json();
      if (errorData.detail) {
        // Backend categorized detail (string or {error,error_code,...})
        if (typeof errorData.detail === 'object' && errorData.detail.error) {
          errorMessage = errorData.detail.error;
        } else if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        }
      }
    } catch {
      // Keep canonical maintainers_alerted — never leak response.statusText.
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

export async function saveConstructionAbstractForm(
  flowId: string,
  data: any,
): Promise<void> {
  const response = await apiCall<any>(
    '/api/construction/abstract/save',
    'POST',
    { ...(flowId ? { flow_id: flowId } : {}), abstract_data: data },
  );

  if (!response.success) {
    throw new Error(response.error || USER_FACING_ERROR_MESSAGES.maintainers_alerted);
  }
}

export async function generateConstructionAbstractPDF(
  flowId: string,
): Promise<void> {
  const API_BASE_URL = process.env.REACT_APP_API_URL || '';

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/construction/abstract/generate`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ ...(flowId ? { flow_id: flowId } : {}) }),
    credentials: 'include',
  });

  if (!response.ok) {
    let errorMessage: string = USER_FACING_ERROR_MESSAGES.maintainers_alerted;
    try {
      const errorData = await response.json();
      if (errorData.detail) {
        if (typeof errorData.detail === 'object' && errorData.detail.error) {
          errorMessage = errorData.detail.error;
        } else if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        }
      }
    } catch {
      // Keep canonical maintainers_alerted.
    }
    throw new Error(errorMessage);
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = url;
  a.download = 'abstract_of_proposals.pdf';
  document.body.appendChild(a);
  a.click();

  // Cleanup
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);

  console.log('Abstract of Proposals PDF downloaded successfully');
}

export async function getConstructionAbstractFormData(
  flowId: string,
): Promise<any> {
  const API_BASE_URL = process.env.REACT_APP_API_URL || '';

  const headers: Record<string, string> = {
    'Cache-Control': 'no-cache',
  };

  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/construction/abstract/${flowId}/form`, {
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    let errorMessage: string = USER_FACING_ERROR_MESSAGES.maintainers_alerted;
    try {
      const errorData = await response.json();
      if (errorData.detail) {
        if (typeof errorData.detail === 'object' && errorData.detail.error) {
          errorMessage = errorData.detail.error;
        } else if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        }
      }
    } catch {
      // Keep canonical maintainers_alerted.
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

export async function removeConstructionAbstractCachedFile(
  flowId: string,
  filename: string,
): Promise<void> {
  const response = await apiCall<any>(
    '/api/construction/abstract/remove-file',
    'POST',
    { ...(flowId ? { flow_id: flowId } : {}), filename },
  );
  if (response?.success === false) {
    const error = new Error(
      response.error || USER_FACING_ERROR_MESSAGES.maintainers_alerted,
    ) as Error & { error_code?: string; technical_detail?: string };
    error.error_code = response.error_code;
    error.technical_detail = response.technical_detail;
    throw error;
  }
}

export async function removeVendorBidsCachedFile(
  flowId: string,
  filename: string,
): Promise<void> {
  await apiCall<any>(
    '/api/vendor-bids/remove-file',
    'POST',
    { ...(flowId ? { flow_id: flowId } : {}), filename },
  );
}

export async function trackCopyQuestions(
  flowId: string | null | undefined,
  prefix: string
): Promise<void> {
  if (!flowId) return;
  try {
    await apiCall(`/api/${prefix}/copy-questions`, 'POST', { flow_id: flowId });
  } catch {
    // best-effort — don't block the copy action
  }
}
